// AdminUserDetail — full edit page for a single player.
//
// Properly rewritten for light + dark with explicit per-element colors
// on every button (Activate / Suspend / Ban / Delete / Save / Reset /
// Credit / Debit / Reset password / Change owner) and every ledger
// chip / amount color so nothing fades into the background in light
// mode.
//
// Sections (single page, gridded on lg+):
//   • Identity card (avatar + status pill + key fields + wallet stat
//     + status buttons + ownership + delete)
//   • Wallet adjust card (credit/debit form with mark-as-recharge)
//   • Password reset card
//   • Ledger card
//   • Profile editor (full form, spans 3 cols)

import { FormEvent, useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import SportLimitsEditor from '@baaligames/shared-ui/admin/components/SportLimitsEditor';
import { motion } from 'motion/react';
import AdminAvatar from '@baaligames/shared-ui/admin/components/AdminAvatar';
import AvatarCropper from '@baaligames/shared-ui/components/AvatarCropper';
import DangerConfirmModal from '@baaligames/shared-ui/components/DangerConfirmModal';
import Icon from '@baaligames/shared-ui/components/Icon';
import PhoneContact from '@baaligames/shared-ui/components/PhoneContact';
import { adminApi, getAdminAccessToken } from '@baaligames/shared-ui/admin/lib/adminApi';
import { compressForUpload } from '@baaligames/shared-ui/lib/imageCompress';
import { INPUT_CLS } from '../components/fieldStyles';
import { useAdminAuth } from '@baaligames/shared-ui/admin/lib/adminAuth';
import { ApiError } from '@baaligames/shared-ui/lib/api';
import { getGame } from '@baaligames/shared-ui/games/registry';
// Phase 16.8: tabbed layout. Same SegmentedTabs primitive used by
// AdminAgentDetail and AgentSubAgentDetail so the player edit page
// feels consistent with agent edit pages.
import { SegmentedTabs, type TabDef } from '@baaligames/shared-ui/admin/components/TabHub';
import LocationCapture, {
  type GeoBag,
  EMPTY_GEO_BAG,
} from '@baaligames/shared-ui/components/LocationCapture';
// Phase R5 — UAID-keyed handoff. Same modal admin + agent use.
import PlayerTransferModal from '@baaligames/shared-ui/admin/components/PlayerTransferModal';
// Entity Redesign Step 3 (2026-05-16): the rebuilt detail page
// reuses the same God-Mode action sheet + Direct-adjust modal as
// the PlayersListPanel kebab so admin's mental model is consistent
// between list-card and profile.
import EntityActionSheet, { type ActionGroup } from '../components/EntityActionSheet';
import DirectWalletAdjustModal from '../components/DirectWalletAdjustModal';
import AgentReassignSheet from '../components/AgentReassignSheet';
import { useAdrenalinePreset, resolveAdrenalineKey, resolveAdrenalineCustom } from '../panels/operations/AdrenalinePanel';

type UserFull = {
  id: number;
  username: string;
  email: string;
  phone: string | null;
  first_name: string | null;
  last_name: string | null;
  birthdate: string | null;
  gender: 'male' | 'female' | 'other' | 'prefer_not' | null;
  // Structured geo. Migration 079 (2026-05-16) dropped the seven
  // legacy free-text address columns; LocationCapture now drives
  // the entire flow from the FK + lat/lng bag below.
  //
  // `pincode` (6-digit string) is JOINed in from the `pincodes`
  // table by UserModel.getForAdmin — it's NOT a column on `users`
  // itself. The string surfaces so LocationCapture can render
  // "Pin code: NNNNNN" without a separate /api/geo/pincode/:id hop.
  pincode: string | null;
  latitude: string | number | null;
  longitude: string | number | null;
  state_id: number | null;
  district_id: number | null;
  area_id: number | null;
  pincode_id: number | null;
  nation: string | null;
  geo_updated_at: string | null;
  profile_image_url: string | null;
  agent_id: number | null;
  owner_username: string | null;
  owner_avatar_url: string | null;
  telegram_chat_id: number | null;
  telegram_username: string | null;
  telegram_verified_at: string | null;
  phone_verified_at: string | null;
  status: 'pending' | 'active' | 'suspended' | 'banned';
  email_verified_at: string | null;
  failed_login_attempts: number;
  locked_until: string | null;
  // Entity Redesign Step 1: test-account marker (migration 078).
  is_test?: 0 | 1 | boolean;
  created_at: string;
  last_login_at: string | null;
  updated_at: string;
  wallet_balance: number;
};

// Tier-2 aggregate response — single round-trip that powers the
// Overview tab's stat tiles. See adminUsers.controller.js#aggregate.
type AggregateResponse = {
  user: UserFull;
  money: {
    wallet_balance: number;
    lifetime_recharge: number;
    lifetime_wager: number;
    lifetime_winnings: number;
  };
  activity: {
    last_match_at: string | null;
    last_match_game: string | null;
    session_count: number;
    audit_count: number;
  };
  referrals: {
    total: number;
    pending: number;
    credited: number;
    rejected: number;
    total_earned: number;
  };
};

type LedgerRow = {
  id: number;
  game: string | null;
  kind: string;
  amount: number;
  note: string | null;
  actor_admin_id: number | null;
  balance_after: number | null;
  session_id: number | null;
  created_at: string;
};

const inr = (n: number) => `₹${(n || 0).toLocaleString('en-IN')}`;

// ── Status pill colors (used both on the identity card and
//    inside the status-button row).
const STATUS_PILL: Record<UserFull['status'], string> = {
  pending:   'bg-amber-50 text-amber-700 ring-1 ring-inset ring-amber-200/60 dark:bg-amber-400/10 dark:text-amber-300 dark:ring-amber-400/30',
  active:    'bg-emerald-50 text-emerald-700 ring-1 ring-inset ring-emerald-200/60 dark:bg-emerald-400/10 dark:text-emerald-300 dark:ring-emerald-400/30',
  suspended: 'bg-orange-50 text-orange-700 ring-1 ring-inset ring-orange-200/60 dark:bg-orange-400/10 dark:text-orange-300 dark:ring-orange-400/30',
  banned:    'bg-rose-50 text-rose-700 ring-1 ring-inset ring-rose-200/60 dark:bg-rose-400/10 dark:text-rose-300 dark:ring-rose-400/30',
};

// ── Ledger row metadata. The text color is the AMOUNT direction,
// shown with proper light + dark contrast.
const KIND_LABEL: Record<string, { label: string; icon: string; pos: boolean | null }> = {
  bet:            { label: 'Match bet',      icon: 'trending_down',          pos: false },
  win:            { label: 'Match prize',    icon: 'trending_up',            pos: true  },
  refund:         { label: 'Refund',         icon: 'undo',                   pos: true  },
  fee:            { label: 'Platform fee',   icon: 'receipt_long',           pos: false },
  admin_credit:   { label: 'Admin credit',   icon: 'add_circle',             pos: true  },
  admin_debit:    { label: 'Admin debit',    icon: 'remove_circle',          pos: false },
  recharge:       { label: 'Recharge',       icon: 'account_balance_wallet', pos: true  },
  referral_bonus: { label: 'Referral bonus', icon: 'card_giftcard',          pos: true  },
  signup_bonus:   { label: 'Signup bonus',   icon: 'stars',                  pos: true  },
  offer_credit:   { label: 'Offer credit',   icon: 'redeem',                 pos: true  },
  withdrawal_hold:   { label: 'Withdrawal',  icon: 'north_east',             pos: false },
  withdrawal_refund: { label: 'Refund',      icon: 'undo',                   pos: true  },
};

function gameBadge(id: string | null): { label: string; cls: string } | null {
  if (!id) return null;
  if (id === 'platform') {
    return { label: 'Platform', cls: 'bg-arena-100/60 text-arena-700 ring-1 ring-inset ring-arena-200/60 dark:bg-arena-800/40 dark:text-neon-200/70 dark:ring-arena-600/40' };
  }
  const g = getGame(id);
  if (!g) return { label: id, cls: 'bg-arena-100/60 text-arena-700 ring-1 ring-inset ring-arena-200/60 dark:bg-arena-800/40 dark:text-neon-200/70 dark:ring-arena-600/40' };
  return {
    label: g.displayName,
    cls: 'bg-violet-50 text-violet-700 ring-1 ring-inset ring-violet-200/60 dark:bg-violet-400/10 dark:text-violet-300 dark:ring-violet-400/30',
  };
}

// ─── Tab definitions (Entity Redesign Step 3, 2026-05-16) ─────────
//
// 6 tabs collapsed to 5. The mapping:
//
//   Old        →   New
//   --------------------------------------
//   Identity   →   merged into Overview (the at-a-glance card)
//   Profile    →   Profile (unchanged — full edit form)
//   Wallet     →   Wallet & Activity  (+ recent matches from /matches)
//   Security   →   Security & Sessions (+ live sessions from /sessions
//                                         + audit feed from /audit)
//   Ownership  →   merged with Operations into 'ops' (admin only)
//   Operations →   merged with Ownership
//
// Tab body now consumes the Tier-2 endpoints (added 2026-05-16):
//   /aggregate · /matches · /sessions · /audit · /referrals
// Each replaces a previously-blank or scattered surface with a single
// purpose-built view.

type PlayerTab = 'overview' | 'profile' | 'wallet' | 'manage';

// Phase 17 carry-over: ops tab still filtered for non-admin role
// (the bulk of its contents — ownership transfer, hard-delete, status
// changes, test-flag toggle — are admin-only privileges that would
// leave the tab body mostly empty for agents).
const PLAYER_TABS: TabDef[] = [
  { key: 'overview', label: 'Overview', icon: 'dashboard' },
  { key: 'profile',  label: 'Profile',  icon: 'person' },
  { key: 'wallet',   label: 'Money',   icon: 'account_balance_wallet' },
  // UX Phase 5 (2026-08-08): Security & sessions + Ownership & ops merged into
  // one "Manage" tab so player + agent detail share the same tab vocabulary
  // (Overview · Profile · Money · Manage). Security content shows to all; the
  // ownership/status/danger content stays admin-only (gated in the body).
  { key: 'manage',   label: 'Manage',  icon: 'manage_accounts' },
];

// ─── Page ──────────────────────────────────────────────────────────

export default function AdminUserDetail() {
  const can = useAdminAuth().canDo;
  const role = useAdminAuth().admin?.role;
  const isAdmin = role === 'admin';
  const { id } = useParams<{ id: string }>();
  const uid = Number(id);
  const nav = useNavigate();
  const [user, setUser] = useState<UserFull | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [flash, setFlash] = useState<string | null>(null);
  const [cropping, setCropping] = useState(false);
  const [tab, setTab] = useState<PlayerTab>('overview');

  // God-Mode action sheet + Direct adjust + Reassign — same primitives
  // the list-card kebab uses, surfaced from the profile header so the
  // mental model carries over between list and detail.
  const [actionsOpen, setActionsOpen] = useState(false);
  const [adjusting,   setAdjusting]   = useState(false);
  const [reassigning, setReassigning] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const { user: u } = await adminApi.get<{ user: UserFull }>(`/admin/users/${uid}`);
      setUser(u);
    } catch (e) {
      setErr((e as ApiError).message || 'Failed to load user');
    } finally {
      setLoading(false);
    }
  }, [uid]);

  useEffect(() => { load(); }, [load]);

  function note(msg: string) {
    setFlash(msg);
    setTimeout(() => setFlash(null), 3000);
  }

  async function uploadPlayerAvatar(rawFile: File) {
    if (!user) return;
    try {
      const file = await compressForUpload(rawFile, { preset: 'avatar' });
      const fd = new FormData();
      fd.append('avatar', file);
      const t = getAdminAccessToken();
      const res = await fetch(`/api/admin/users/${user.id}/avatar`, {
        method: 'POST', credentials: 'include',
        headers: t ? { authorization: `Bearer ${t}` } : {},
        body: fd,
      });
      const j = await res.json();
      if (!j.ok) throw new Error(j.error?.message || 'Upload failed');
      note('Photo updated');
      await load();
    } catch (e) {
      alert((e as Error).message);
      throw e;
    }
  }

  // Convenience: any user mutation (status, force-verify, unlink-tg,
  // etc.) shares the same after-effect — refresh the user row + flash
  // a confirmation. Centralised so the action-sheet items stay terse.
  async function mutate(path: string, body: unknown, message: string) {
    try {
      await adminApi.post(path, body);
      note(message);
      await load();
    } catch (e) {
      alert((e as ApiError).message);
    }
  }

  return (
    <div className="max-w-screen-2xl mx-auto px-2 sm:px-3 lg:px-6 py-3 lg:py-4 space-y-3 pb-12">
      {/* Back */}
      <div className="px-2 lg:px-4 pt-1">
        <Link to="/people/players"
              className="inline-flex items-center gap-1 text-[12px] font-semibold
                         text-arena-700 dark:text-neon-200/80 hover:text-neutral-900
                         dark:text-white/55 dark:hover:text-white transition">
          <Icon name="arrow_back" size={14} /> Back to Players
        </Link>
      </div>

      {loading && (
        <div className="px-2 lg:px-4">
          <div className="rounded-2xl bg-white dark:bg-arena-700/40 ring-1 ring-inset ring-arena-200/50 dark:ring-arena-600/40 p-10 text-center text-[13px] text-arena-600 dark:text-neon-200/70">
            <Icon name="progress_activity" size={20} className="animate-spin inline-block mr-1.5" /> Loading…
          </div>
        </div>
      )}

      {err && <Banner tone="error">{err}</Banner>}

      {flash && (
        <motion.div initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }}>
          <Banner tone="success"><Icon name="check_circle" size={14} filled className="inline -mt-0.5 mr-1" /> {flash}</Banner>
        </motion.div>
      )}

      {!loading && user && (
        <div className="px-2 lg:px-4 space-y-3">
          {/* ─── Pinned identity header ───────────────────────────
              Sticky so the admin keeps the "who am I editing?"
              context on screen while they scroll through long tab
              bodies (the ledger and audit can run hundreds of rows).
              The kebab top-right is the same EntityActionSheet
              primitive the list card uses; it carries every Tier-1
              verb so the profile page is operationally complete on
              its own. */}
          <PinnedHeader
            user={user}
            canEditAvatar={!!can('users','edit')}
            onPhotoEdit={() => setCropping(true)}
            onOpenActions={() => setActionsOpen(true)}
          />

          {/* Tab strip — only the Ownership/Ops tab is admin-gated
              (the other 4 work for both roles). */}
          <SegmentedTabs
            tabs={PLAYER_TABS}
            activeKey={tab}
            onTabChange={(k) => setTab(k as PlayerTab)}
          />

          {/* ─── Tab body ─────────────────────────────────── */}

          {tab === 'overview' && (
            <OverviewTab uid={user.id} user={user} onAdjust={() => setAdjusting(true)} />
          )}

          {tab === 'profile' && (
            <ProfileEditCard user={user} onSaved={load} note={note} />
          )}

          {tab === 'wallet' && (
            <div className="space-y-3">
              {/* Wallet & activity — combines the legacy ledger with
                  the new /matches feed and a single "Adjust wallet"
                  CTA that opens DirectWalletAdjustModal (the rich
                  identity-card variant — same as list-card kebab).
                  Mobile layout stacks the Adjust button below the
                  description so the helper text isn't squeezed into
                  4 wrapped lines by a side-by-side button. */}
              <Card>
                <div className="flex items-start gap-3 mb-3">
                  <span className="grid place-items-center w-9 h-9 rounded-xl shrink-0
                                   bg-violet-50 text-violet-700 ring-1 ring-inset ring-violet-200/60
                                   dark:bg-violet-400/15 dark:text-violet-300 dark:ring-violet-400/30">
                    <Icon name="account_balance_wallet" size={18} filled />
                  </span>
                  <div className="min-w-0 flex-1">
                    <h3 className="font-bold text-[14px] text-neutral-900 dark:text-white leading-tight">
                      Adjust wallet
                    </h3>
                    <p className="text-[11.5px] text-arena-600 dark:text-neon-200/70 mt-1 leading-snug">
                      Direct credit / debit with full audit log + ledger writes.
                      Pick Platform source for a mint/burn or Agent pool to debit
                      <span className="font-semibold"> @{user.owner_username || 'owner'}</span>.
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setAdjusting(true)}
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-1.5 h-9 px-4 rounded-full
                             bg-violet-600 hover:bg-violet-500 active:bg-violet-700
                             text-white text-[12.5px] font-bold
                             shadow-[0_2px_10px_rgba(139,92,246,0.45)] transition active:scale-95"
                >
                  <Icon name="tune" size={14} /> Open direct credit / debit
                </button>
              </Card>
              {/* Per-player max betting limits — category caps (casino/
                  board/small) + the 4 cricket-exchange caps. Same API
                  the agent-end inline editor uses; admin scope covers
                  any player. */}
              <SportLimitsEditor userId={user.id} />
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-4">
                <LedgerCard userId={user.id} />
                <RecentMatchesCard uid={user.id} />
              </div>
            </div>
          )}

          {/* MANAGE tab (UX Phase 5): security & sessions — shown to everyone
              who can view this player. */}
          {tab === 'manage' && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-3">
              <PasswordResetCard userId={user.id} note={note} />
              <SessionsCard uid={user.id} />
              <div className="lg:col-span-2">
                <AuditCard uid={user.id} />
              </div>
            </div>
          )}

          {/* MANAGE tab — ownership · status · danger zone. Admin-only, same
              gate the old "Ownership & ops" tab carried. */}
          {tab === 'manage' && isAdmin && (
            <div className="space-y-3">
              {/* OwnershipCard owns its own <Card> wrapper now — was
                  previously appended to an Identity card; the legacy
                  inner heading + border-top has been removed so we no
                  longer get the double-header / cramped row that
                  showed up on phones. */}
              <OwnershipCard user={user} reload={load} note={note} />

              <Card>
                <h3 className="font-semibold text-[14px] text-neutral-900 dark:text-white inline-flex items-center gap-2 mb-3">
                  <Icon name="manage_accounts" size={16} className="text-violet-500" />
                  Account status
                </h3>
                <p className="text-[11.5px] text-arena-600 dark:text-neon-200/70 mb-3 leading-snug">
                  Active means the player can log in and play. Suspend blocks logins until you flip back.
                  Ban is a stronger lock — keep it for policy violations.
                </p>
                <StatusButtons user={user} reload={load} note={note} />
              </Card>

              {/* Test-flag toggle — admin only. Marks the row so
                  analytics endpoints (platformPnl, leaderboards,
                  homeSummary) exclude this player's wallet activity
                  by default. */}
              {isAdmin && (
                <Card>
                  <h3 className="font-semibold text-[14px] text-neutral-900 dark:text-white inline-flex items-center gap-2 mb-3">
                    <Icon name="science" size={16} className="text-amber-500" />
                    Test-account flag
                  </h3>
                  <p className="text-[11.5px] text-arena-600 dark:text-neon-200/70 mb-3">
                    Mark this player as a test/QA account. Wallet, login,
                    and match systems remain unaffected — analytics
                    endpoints just hide test rows by default
                    (admin can pass <code>?include_test=true</code> for the
                    forensic view).
                  </p>
                  <button
                    onClick={() => mutate(`/admin/users/${user.id}/test-flag`,
                                          { is_test: !user.is_test },
                                          user.is_test ? 'Unmarked as test account' : 'Marked as test account')}
                    className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-[12.5px] font-semibold transition active:scale-95
                      ${user.is_test
                        ? 'bg-amber-100 text-amber-800 ring-1 ring-inset ring-amber-300/60 hover:bg-amber-50 dark:bg-amber-500/15 dark:text-amber-300 dark:ring-amber-400/30'
                        : 'bg-white text-arena-700 ring-1 ring-inset ring-arena-200/60 hover:bg-arena-50 dark:bg-arena-700/40 dark:text-neon-200/75 dark:ring-arena-600/40'}`}>
                    <Icon name={user.is_test ? 'science_off' : 'science'} size={14} filled />
                    {user.is_test ? 'Unmark test account' : 'Mark as test account'}
                  </button>
                </Card>
              )}

              {/* Phase B (plan §4.7): the player's Luck card — what the
                  spotlight layer currently thinks of this player and the
                  two admin verbs (Exclude from spotlight / Reset to normal).
                  Admin-only: the backend gates POST with requireAdminRole. */}
              {isAdmin && <LuckCard user={user} note={note} />}

              {isAdmin && (
                <Card>
                  <h3 className="font-semibold text-[14px] text-rose-700 dark:text-rose-300 inline-flex items-center gap-2 mb-3">
                    <Icon name="delete_forever" size={16} />
                    Danger zone
                  </h3>
                  <p className="text-[11.5px] text-arena-600 dark:text-neon-200/70 mb-3">
                    Hard-deleting removes the user record. The wallet ledger is preserved for audit, but
                    the player will lose all access. This action cannot be undone.
                  </p>
                  <DeleteButton user={user} onDeleted={() => nav('/people/players', { replace: true })} note={note} />
                </Card>
              )}
            </div>
          )}
        </div>
      )}

      {cropping && user && (
        <AvatarCropper
          title={`Adjust photo for @${user.username}`}
          onUpload={uploadPlayerAvatar}
          onClose={() => setCropping(false)}
        />
      )}

      {/* ─── God-Mode action sheet ──────────────────────────────
          Mounted at root so it floats above the tab body (z-80).
          Items map 1:1 with the list-card kebab so admin doesn't
          relearn the verb vocabulary moving between list and detail. */}
      {actionsOpen && user && (
        <PlayerActionSheet
          user={user}
          isAdmin={isAdmin}
          onClose={() => setActionsOpen(false)}
          onAdjust={() => { setActionsOpen(false); setAdjusting(true); }}
          onReassign={() => { setActionsOpen(false); setReassigning(true); }}
          onMutate={async (path, body, msg) => {
            await mutate(path, body, msg);
          }}
          onDelete={async () => {
            try {
              await adminApi.del(`/admin/users/${user.id}`);
              nav('/people/players', { replace: true });
            } catch (e) { alert((e as ApiError).message); }
          }}
        />
      )}

      {adjusting && user && (
        <DirectWalletAdjustModal
          subject={{
            kind: 'player',
            id: user.id,
            username: user.username,
            displayName: (user.first_name || user.last_name)
              ? `${user.first_name || ''} ${user.last_name || ''}`.trim()
              : user.username,
            avatarUrl: user.profile_image_url,
            agentId: user.agent_id,
            agentUsername: user.owner_username,
            walletBalance: user.wallet_balance,
          }}
          onClose={() => setAdjusting(false)}
          onDone={() => { setAdjusting(false); void load(); }}
        />
      )}

      {reassigning && user && (
        <AgentReassignSheet
          subject={{ kind: 'player', label: user.username, currentParentId: user.agent_id }}
          detach={{ label: 'Detach to platform', description: 'Remove agent ownership. Admin manages directly.', icon: 'shield' }}
          onClose={() => setReassigning(false)}
          onAssign={async (newParentId, reason) => {
            await adminApi.post(`/admin/users/${user.id}/reassign-agent`, {
              new_agent_id: newParentId,
              reason: reason || undefined,
            });
            setReassigning(false);
            void load();
          }}
        />
      )}
    </div>
  );
}

// ─── Reusable shells ───────────────────────────────────────────────

function Card({ children }: { children: React.ReactNode }) {
  // 2026-05-16 mobile pass: padding is now `p-4 sm:p-5` — 16px on
  // phones (where every pixel of horizontal room matters) and 20px
  // from sm+ where there's air to spare. The old hardcoded p-5 was
  // robbing roughly 8px from each side on a 414px screen.
  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25 }}
      className="rounded-2xl p-4 sm:p-5
                 bg-white dark:bg-arena-700/40
                 ring-1 ring-inset ring-arena-200/50 dark:ring-arena-600/40
                 shadow-[0_1px_3px_rgba(0,0,0,0.04)] dark:shadow-none">
      {children}
    </motion.div>
  );
}

function Banner({ tone, children }: { tone: 'error' | 'success' | 'warn' | 'info'; children: React.ReactNode }) {
  const M = {
    error:   'bg-rose-50 border-rose-200 text-rose-800 dark:bg-rose-500/10 dark:border-rose-400/30 dark:text-rose-200',
    success: 'bg-emerald-50 border-emerald-200 text-emerald-800 dark:bg-emerald-500/10 dark:border-emerald-400/30 dark:text-emerald-200',
    warn:    'bg-amber-50 border-amber-200 text-amber-800 dark:bg-amber-500/10 dark:border-amber-400/30 dark:text-amber-200',
    info:    'bg-arena-50/60 ring-arena-200/60 text-arena-700 dark:bg-arena-800/40 dark:ring-arena-600/40 dark:text-neon-200/75',
  } as const;
  return <div className={`mx-2 lg:mx-4 rounded-2xl border px-4 py-3 text-[13px] ${M[tone]}`}>{children}</div>;
}

function Info({ k, v, accent }: { k: string; v: string; accent?: 'ok' | 'warn' }) {
  const valCls = accent === 'ok'   ? 'text-emerald-700 dark:text-emerald-300 font-semibold'
              : accent === 'warn' ? 'text-amber-700 dark:text-amber-300 font-semibold'
              : 'text-neutral-900 dark:text-white';
  return (
    <div className="flex items-center justify-between gap-2 py-1 border-b border-arena-200/40 dark:border-arena-700/40 last:border-b-0">
      <dt className="text-arena-600 dark:text-neon-200/70">{k}</dt>
      <dd className={`truncate ml-2 ${valCls}`}>{v}</dd>
    </div>
  );
}

// ─── Status buttons (Activate / Suspend / Ban) ─────────────────────
//
// All three buttons are SOLID with proper light+dark colors. The
// active button is shown highlighted; inactive ones are visible
// outlined buttons that the admin can tap to switch.

function StatusButtons({ user, reload, note }: { user: UserFull; reload: () => void; note: (s: string) => void }) {
  const [busy, setBusy] = useState<string | null>(null);
  // Phase 19 / Batch 2 (N7): Suspend + Ban are destructive; require an
  // explicit confirmation modal that ALSO captures a free-text reason
  // and forwards it to the backend so it lands in admin.user_status_changed
  // audit-log meta. The backend already accepts + records `reason` —
  // see adminUsers.controller.js setStatus. Activate is a safe action
  // and bypasses the modal.
  const [pendingStatus, setPendingStatus] = useState<UserFull['status'] | null>(null);

  async function performStatusChange(status: UserFull['status'], reason?: string) {
    setBusy(status);
    try {
      await adminApi.post(`/admin/users/${user.id}/status`, { status, reason: reason || undefined });
      note(`User is now ${status}${reason ? ` — reason: "${reason}"` : ''}`);
      setPendingStatus(null);
      reload();
    } catch (e) {
      // Re-throw so DangerConfirmModal can render the error inline; the
      // catch sites inside the modal keep the modal open. For Activate
      // (no modal), the throw bubbles up to React — wrap to alert there.
      throw e;
    } finally { setBusy(null); }
  }

  async function setStatus(status: UserFull['status']) {
    if (status === 'suspended' || status === 'banned') {
      // Open the confirmation modal; actual call happens on confirm.
      setPendingStatus(status);
      return;
    }
    // Safe path (Activate) — fire immediately.
    try { await performStatusChange(status); }
    catch (e) { alert((e as ApiError).message); }
  }

  // Each button has TWO color modes: "current" (the user's current
  // state — fully filled) and "available" (a tap to change to that
  // state — outlined but visible).
  function Btn({ label, status, currentClass, availableClass, icon }: {
    label: string;
    status: UserFull['status'];
    currentClass: string;
    availableClass: string;
    icon: string;
  }) {
    const isCurrent = user.status === status;
    return (
      <button
        onClick={() => setStatus(status)}
        disabled={busy !== null || isCurrent}
        className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-[12.5px] font-semibold transition active:scale-95
                    ${isCurrent ? currentClass : availableClass}
                    disabled:cursor-not-allowed`}
      >
        {busy === status
          ? <Icon name="progress_activity" size={14} className="animate-spin" />
          : <Icon name={icon} size={14} filled />}
        {label}
        {isCurrent && <span className="text-[9.5px] uppercase opacity-75">current</span>}
      </button>
    );
  }

  return (
    <div className="flex flex-wrap gap-2">
      <Btn
        label="Activate" status="active" icon="check_circle"
        currentClass="bg-emerald-600 text-white shadow-[0_2px_10px_rgba(16,185,129,0.4)] disabled:opacity-100"
        availableClass="bg-emerald-50 text-emerald-700 ring-1 ring-inset ring-emerald-200/60 hover:bg-emerald-100
                        dark:bg-emerald-400/15 dark:text-emerald-300 dark:border-emerald-400/30 dark:hover:bg-emerald-400/25"
      />
      <Btn
        label="Suspend" status="suspended" icon="pause_circle"
        currentClass="bg-orange-600 text-white shadow-[0_2px_10px_rgba(234,88,12,0.4)] disabled:opacity-100"
        availableClass="bg-orange-50 text-orange-700 ring-1 ring-inset ring-orange-200/60 hover:bg-orange-100
                        dark:bg-orange-400/15 dark:text-orange-300 dark:border-orange-400/30 dark:hover:bg-orange-400/25"
      />
      <Btn
        label="Ban" status="banned" icon="block"
        currentClass="bg-rose-600 text-white shadow-[0_2px_10px_rgba(244,63,94,0.4)] disabled:opacity-100"
        availableClass="bg-rose-50 text-rose-700 ring-1 ring-inset ring-rose-200/60 hover:bg-rose-100
                        dark:bg-rose-400/15 dark:text-rose-300 dark:border-rose-400/30 dark:hover:bg-rose-400/25"
      />

      {/* Batch 2 N7: confirmation gate for Suspend / Ban with reason
          capture. Reason flows through to admin.user_status_changed
          audit-log meta. */}
      {pendingStatus !== null && (
        <DangerConfirmModal
          open={true}
          onClose={() => setPendingStatus(null)}
          onConfirm={(reason) => performStatusChange(pendingStatus, reason)}
          title={pendingStatus === 'suspended'
            ? `Suspend @${user.username}?`
            : `Ban @${user.username}?`}
          description={pendingStatus === 'suspended'
            ? "Player can't log in until you flip them back to Active. All live sessions will be revoked immediately."
            : 'Strongest lock — keep it for policy violations. Player loses access and live sessions are revoked. The wallet ledger is preserved for audit.'}
          confirmLabel={pendingStatus === 'suspended' ? 'Suspend' : 'Ban'}
          cancelLabel="Cancel"
          reasonLabel="Reason (logged to audit)"
          reasonPlaceholder={pendingStatus === 'suspended'
            ? 'e.g. unresolved chargeback dispute'
            : 'e.g. multi-account collusion confirmed in match #1234'}
          reasonRequired={pendingStatus === 'banned'}
        />
      )}
    </div>
  );
}

// ─── Delete button (danger zone) ───────────────────────────────────

function DeleteButton({ user, onDeleted, note }: { user: UserFull; onDeleted: () => void; note: (s: string) => void }) {
  // Admin-only — if non-admin, render nothing. Backend also gates
  // DELETE /api/admin/users/:id with requireAdminRole; this just
  // hides the UI so agents don't see a button that would 403.
  const { admin } = useAdminAuth();
  if (admin?.role !== 'admin') return null;
  const [confirmText, setConfirmText] = useState('');
  const [busy, setBusy] = useState(false);
  const expected = user.username;
  async function doDelete() {
    if (confirmText !== expected) return;
    setBusy(true);
    try {
      await adminApi.del(`/admin/users/${user.id}`);
      note('User deleted');
      onDeleted();
    } catch (e) {
      alert((e as ApiError).message);
    } finally { setBusy(false); }
  }
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider font-bold text-rose-600 dark:text-rose-400 mb-1">Danger zone</div>
      <p className="text-[11.5px] text-arena-600 dark:text-neon-200/70 mb-2">
        Permanently delete this account. This cannot be undone. Type
        {' '}<b className="text-rose-700 dark:text-rose-300 font-mono">{expected}</b>{' '}
        to confirm.
      </p>
      <div className="flex items-center gap-2">
        <input
          value={confirmText}
          onChange={(e) => setConfirmText(e.target.value)}
          placeholder={expected}
          className={inputCls + ' font-mono flex-1'}
        />
        <button
          onClick={doDelete}
          disabled={busy || confirmText !== expected}
          className="inline-flex items-center gap-1 px-3 py-2 rounded-full text-[12.5px] font-bold
                     bg-rose-600 hover:bg-rose-500 active:bg-rose-700
                     text-white shadow-[0_2px_10px_rgba(244,63,94,0.4)]
                     disabled:opacity-40 disabled:cursor-not-allowed
                     transition active:scale-95"
        >
          {busy ? <Icon name="progress_activity" size={13} className="animate-spin" /> : <Icon name="delete_forever" size={13} />}
          Delete
        </button>
      </div>
    </div>
  );
}

// ─── Luck card (Phase B, plan §4.7) ────────────────────────────────
//
// Reads GET /admin/users/:id/luck (shared/src/core/routes/adminLuck.routes.js
// getUserLuckForAdmin) — shape:
//   { available:boolean, reason?, user:{id,username,is_test,district_id,agent_id},
//     star: <player_luck '*' row minus the P² blobs> | null, games:[rows],
//     roster:[{id,window_start,district_id,burst_target,armed_at,fired_at,
//              fired_amount,fired_game,status}], lastInfluenced:{decided_at,game,
//              mode,stream,cost,llr,share}|null, events:[…], lastAdminAction:
//              {actor_id,meta:{action,…},created_at}|null, excluded:boolean,
//     luckClass:'normal'|'golden'|'relief'|'cooldown', classUntil }
//   available:false (migration 165 not applied) ⇒ the "not available" state.
// It offers the admin verbs the plan names: "Exclude from spotlight" (never
// rostered golden, never favoured), "Include again", and "Reset to normal"
// (drops class/cooldown/arming). Every verb writes an audit_log row
// server-side; each sits behind a DangerConfirmModal. (The POST accepts only
// {action} — no free-text reason is recorded, so none is asked for.)
//
// Whether the spotlight is ON at all is NOT part of that response — the card
// reads the platform preset (GET /admin/platform/adrenaline_preset, the same
// hook the Game-money hub uses) so it can say plainly that nothing is being
// tracked while the preset is Off (Phase B ships dark).

type LuckClass = 'normal' | 'golden' | 'relief' | 'cooldown';
const LUCK_CLASSES: readonly LuckClass[] = ['normal', 'golden', 'relief', 'cooldown'];

type LuckRowShape = {
  luck_class?: string | null;
  class_until?: string | null;
  excluded_by_admin?: 0 | 1 | boolean | null;
  is_drifting?: 0 | 1 | boolean | null;
  district_id?: number | null;
  turnover_7d?: number | null;
  net_7d?: number | null;
  rounds_7d?: number | null;
  influence_count_7d?: number | null;
  lifetime_rounds?: number | null;
  net_life?: number | null;
  updated_at?: string | null;
};

type LuckDecisionShape = {
  decided_at?: string | null;
  game?: string | null;
  mode?: string | null;
  stream?: string | null;
  cost?: number | null;
  llr?: number | null;
  share?: number | null;
};

type LuckRosterShape = {
  id?: number;
  window_start?: string | null;
  district_id?: number | null;
  burst_target?: number | null;
  armed_at?: string | null;
  fired_at?: string | null;
  fired_amount?: number | null;
  fired_game?: string | null;
  status?: string | null;
};

type LuckAdminActionShape = {
  actor_id?: number | null;
  created_at?: string | null;
  meta?: { action?: string } | string | null;
};

type LuckGetResp = {
  available?: boolean;
  reason?: string;
  user?: { id: number; username: string; is_test?: 0 | 1 | boolean } | null;
  star?: LuckRowShape | null;
  games?: LuckRowShape[];
  roster?: LuckRosterShape[];
  lastInfluenced?: LuckDecisionShape | null;
  events?: unknown[];
  lastAdminAction?: LuckAdminActionShape | null;
  excluded?: boolean;
  luckClass?: string | null;
  classUntil?: string | null;
  // Tolerated aliases (earlier drafts of the route) — never the primary source.
  luck?: LuckRowShape | null;
  row?: LuckRowShape | null;
  lastDecision?: LuckDecisionShape | null;
};

/** Pure derivation of what the Luck card shows from the GET body. Exported for the WP5b smoke test. */
export function deriveLuckView(resp: LuckGetResp | null | undefined) {
  const row: LuckRowShape | null = resp ? (resp.star ?? resp.luck ?? resp.row ?? null) : null;
  const decision: LuckDecisionShape | null = resp ? (resp.lastInfluenced ?? resp.lastDecision ?? null) : null;
  const excluded = typeof resp?.excluded === 'boolean' ? resp.excluded : Number(row?.excluded_by_admin ?? 0) === 1;
  const klassRaw = String(resp?.luckClass ?? row?.luck_class ?? 'normal').toLowerCase();
  const klass: LuckClass = (LUCK_CLASSES as readonly string[]).includes(klassRaw) ? (klassRaw as LuckClass) : 'normal';
  const classUntil = resp?.classUntil ?? row?.class_until ?? null;
  const drifting = Number(row?.is_drifting ?? 0) === 1 || row?.is_drifting === true;
  const unavailable = resp?.available === false;
  const roster = Array.isArray(resp?.roster) ? resp.roster : [];
  const armedSlot = roster.find((r) => String(r?.status).toLowerCase() === 'armed') ?? null;
  const lastFired = roster.find((r) => String(r?.status).toLowerCase() === 'fired') ?? null;
  const lastAdminAction = resp?.lastAdminAction ?? null;
  const lastAdminVerb = lastAdminAction
    ? (typeof lastAdminAction.meta === 'object' && lastAdminAction.meta ? String(lastAdminAction.meta.action ?? '') : '')
    : '';
  return { row, decision, excluded, klass, classUntil, drifting, unavailable, armedSlot, lastFired, lastAdminAction, lastAdminVerb };
}

const LUCK_CLASS_META: Record<LuckClass, { label: string; plain: string; cls: string; icon: string }> = {
  normal:   { label: 'Normal',          plain: 'Plain chance — no favour either way.',
              cls: 'bg-arena-100/70 text-arena-700 ring-arena-200/60 dark:bg-arena-800/50 dark:text-neon-200/80 dark:ring-arena-600/40', icon: 'casino' },
  golden:   { label: 'Spotlight lined up', plain: 'Picked for one memorable win in their district this week. Nothing changes for them until it lands.',
              cls: 'bg-violet-50 text-violet-700 ring-violet-200/60 dark:bg-violet-400/15 dark:text-violet-300 dark:ring-violet-400/30', icon: 'auto_awesome' },
  relief:   { label: 'Comeback due',    plain: 'On a bad run — owed one visible comeback win at the game\'s normal odds.',
              cls: 'bg-cyan-50 text-cyan-700 ring-cyan-200/60 dark:bg-cyan-400/15 dark:text-cyan-300 dark:ring-cyan-400/30', icon: 'healing' },
  cooldown: { label: 'Resting',         plain: 'Recently had a spotlight win — back to plain chance, and not eligible again for a while.',
              cls: 'bg-amber-50 text-amber-800 ring-amber-200/70 dark:bg-amber-400/15 dark:text-amber-300 dark:ring-amber-400/30', icon: 'bedtime' },
};

function LuckCard({ user, note }: { user: UserFull; note: (s: string) => void }) {
  const [resp, setResp] = useState<LuckGetResp | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [unavailable, setUnavailable] = useState(false);
  const [pending, setPending] = useState<'exclude' | 'include' | 'reset' | null>(null);

  const load = useCallback(async () => {
    setLoading(true); setErr(null); setUnavailable(false);
    try {
      const r = await adminApi.get<LuckGetResp>(`/admin/users/${user.id}/luck`);
      setResp(r ?? null);
    } catch (e) {
      const ae = e as ApiError;
      // 404 = the route is not deployed yet (Phase B backend lands in
      // parallel); say so calmly rather than shouting an error.
      if (ae?.status === 404) setUnavailable(true);
      else setErr(ae?.message || 'Could not load luck data');
    } finally { setLoading(false); }
  }, [user.id]);

  useEffect(() => { void load(); }, [load]);

  // Platform-wide preset (Off = nothing tracked). Failure ⇒ no banner, no error.
  const preset = useAdrenalinePreset(true);
  const presetKey = resolveAdrenalineKey(preset.data);
  const off = presetKey === 'off' || (presetKey === 'custom' && resolveAdrenalineCustom(preset.data) === 0);

  const view = deriveLuckView(resp);
  const { row, decision, excluded, klass, classUntil, drifting, armedSlot, lastFired, lastAdminAction, lastAdminVerb } = view;
  const meta = LUCK_CLASS_META[klass];
  const notAvailable = unavailable || view.unavailable;
  const isTestAccount = !!user.is_test;

  async function act(action: 'exclude' | 'include' | 'reset') {
    await adminApi.post(`/admin/users/${user.id}/luck`, { action });
    note(action === 'exclude' ? 'Excluded from the spotlight'
      : action === 'include' ? 'Back in the spotlight pool'
      : 'Luck reset to normal');
    setPending(null);
    await load();
  }

  const btn = 'inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-[12.5px] font-semibold transition active:scale-95 disabled:opacity-40 disabled:cursor-not-allowed';
  const fmtWhen = (ts: string | null | undefined) => ts
    ? new Date(ts).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }) : null;

  return (
    <Card>
      <h3 className="font-semibold text-[14px] text-neutral-900 dark:text-white inline-flex items-center gap-2 mb-1">
        <Icon name="auto_awesome" size={16} className="text-violet-600 dark:text-violet-300" />
        Luck &amp; spotlight
      </h3>
      <p className="text-[11.5px] text-arena-600 dark:text-neon-200/70 mb-3 leading-snug">
        What the spotlight layer currently thinks of this player. Excluding them means they are never
        picked for a spotlight win and never favoured — their odds stay plain chance. Nothing here touches
        their wallet.
      </p>

      {loading && (
        <div className="text-[12px] text-arena-600 dark:text-neon-200/70 py-2">
          <Icon name="progress_activity" size={14} className="animate-spin inline-block mr-1" /> Loading…
        </div>
      )}
      {!loading && notAvailable && (
        <div className="text-[12px] text-arena-600 dark:text-neon-200/70 py-2">
          Luck data is not available on this server yet.
        </div>
      )}
      {!loading && err && <Banner tone="error">{err}</Banner>}

      {!loading && !notAvailable && !err && (
        <div className="space-y-3">
          {off && (
            <div className="rounded-xl px-3 py-2 text-[12px]
                            bg-arena-50/60 text-arena-700 ring-1 ring-inset ring-arena-200/60
                            dark:bg-arena-800/40 dark:text-neon-200/75 dark:ring-arena-600/40">
              The spotlight is switched <b>off</b> platform-wide, so nothing is being tracked or favoured for anyone.
              An exclusion set now still holds when it is switched on.
            </div>
          )}
          {isTestAccount && (
            <div className="rounded-xl px-3 py-2 text-[12px]
                            bg-amber-50 text-amber-800 ring-1 ring-inset ring-amber-200/60
                            dark:bg-amber-500/10 dark:text-amber-200 dark:ring-amber-400/30">
              Test account — the spotlight always treats test accounts as plain chance, whatever is shown here.
            </div>
          )}

          {/* Class + chips */}
          <div className="flex flex-wrap items-center gap-1.5">
            <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-bold ring-1 ring-inset ${meta.cls}`}>
              <Icon name={meta.icon} size={12} filled /> {meta.label}
            </span>
            {excluded && (
              <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-bold ring-1 ring-inset
                               bg-rose-50 text-rose-700 ring-rose-200/60 dark:bg-rose-400/15 dark:text-rose-300 dark:ring-rose-400/30">
                <Icon name="person_off" size={12} filled /> Excluded by admin
              </span>
            )}
            {drifting && (
              <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-bold ring-1 ring-inset
                               bg-orange-50 text-orange-700 ring-orange-200/60 dark:bg-orange-400/15 dark:text-orange-300 dark:ring-orange-400/30"
                    title="Betting noticeably smaller than their usual this week">
                <Icon name="trending_down" size={12} filled /> Drifting
              </span>
            )}
          </div>
          <p className="text-[11.5px] text-arena-600 dark:text-neon-200/70 -mt-1">{meta.plain}</p>

          {!row ? (
            <p className="text-[12px] text-arena-600 dark:text-neon-200/70">
              {off
                ? 'No luck record — nothing is tracked while the spotlight is off.'
                : 'No luck record yet — this player has not played a spotlight-eligible game since it was switched on.'}
            </p>
          ) : (
            <dl className="text-[12px] rounded-xl px-3 py-1.5 bg-arena-50/40 dark:bg-arena-800/40 ring-1 ring-inset ring-arena-200/50 dark:ring-arena-600/40">
              {classUntil && klass !== 'normal' && <Info k="Until" v={fmtWhen(classUntil) || '—'} />}
              {armedSlot && (
                <Info k="Lined up" v={`since ${fmtWhen(armedSlot.armed_at) || '—'}${armedSlot.burst_target != null ? ` · about ${inr(Number(armedSlot.burst_target))}` : ''}`} />
              )}
              {lastFired && (
                <Info k="Last spotlight win" v={`${fmtWhen(lastFired.fired_at) || '—'}${lastFired.fired_amount != null ? ` · ${inr(Number(lastFired.fired_amount))}` : ''}${lastFired.fired_game ? ` · ${getGame(String(lastFired.fired_game))?.displayName ?? lastFired.fired_game}` : ''}`} />
              )}
              <Info k="Last 7 days" v={`${Number(row.rounds_7d ?? 0).toLocaleString('en-IN')} rounds · staked ${inr(Number(row.turnover_7d ?? 0))} · net ${Number(row.net_7d ?? 0) >= 0 ? '+' : ''}${inr(Number(row.net_7d ?? 0))}`}
                    accent={Number(row.net_7d ?? 0) < 0 ? 'warn' : undefined} />
              <Info k="Rounds the spotlight touched (7d)" v={String(row.influence_count_7d ?? 0)} />
              {row.district_id != null && <Info k="District" v={`#${row.district_id}`} />}
              <Info k="Last influenced decision" v={decision
                ? `${fmtWhen(decision.decided_at) || '—'}${decision.game ? ` · ${getGame(String(decision.game))?.displayName ?? decision.game}` : ''}${decision.stream ? ` · ${decision.stream}` : decision.mode ? ` · ${decision.mode}` : ''}${decision.cost != null ? ` · cost ${inr(Number(decision.cost))}` : ''}`
                : 'none'} />
              {row.updated_at && <Info k="Updated" v={fmtWhen(row.updated_at) || '—'} />}
            </dl>
          )}
          {lastAdminAction && (
            <p className="text-[11.5px] text-arena-600 dark:text-neon-200/65">
              Last admin action: <b className="capitalize">{lastAdminVerb || 'luck change'}</b>
              {lastAdminAction.created_at ? ` · ${fmtWhen(lastAdminAction.created_at)}` : ''}
            </p>
          )}

          {/* Verbs */}
          <div className="flex flex-wrap gap-2">
            {excluded ? (
              <button onClick={() => setPending('include')}
                className={`${btn} bg-emerald-50 text-emerald-700 ring-1 ring-inset ring-emerald-200/60 hover:bg-emerald-100
                            dark:bg-emerald-400/15 dark:text-emerald-300 dark:ring-emerald-400/30 dark:hover:bg-emerald-400/25`}>
                <Icon name="person_add" size={14} filled /> Include in spotlight again
              </button>
            ) : (
              <button onClick={() => setPending('exclude')}
                className={`${btn} bg-rose-50 text-rose-700 ring-1 ring-inset ring-rose-200/60 hover:bg-rose-100
                            dark:bg-rose-400/15 dark:text-rose-300 dark:ring-rose-400/30 dark:hover:bg-rose-400/25`}>
                <Icon name="person_off" size={14} filled /> Exclude from spotlight
              </button>
            )}
            {/* Reset has something to do when a class/cooldown/arming exists. */}
            <button onClick={() => setPending('reset')}
              disabled={!row || (klass === 'normal' && !classUntil && !armedSlot)}
              className={`${btn} bg-white text-arena-700 ring-1 ring-inset ring-arena-200/60 hover:bg-arena-50
                          dark:bg-arena-700/40 dark:text-neon-200/75 dark:ring-arena-600/40 dark:hover:bg-arena-700/60`}>
              <Icon name="restart_alt" size={14} /> Reset to normal
            </button>
          </div>
        </div>
      )}

      {pending !== null && (
        <DangerConfirmModal
          open={true}
          onClose={() => setPending(null)}
          onConfirm={() => act(pending)}
          title={pending === 'exclude' ? `Exclude @${user.username} from the spotlight?`
            : pending === 'include' ? `Include @${user.username} in the spotlight again?`
            : `Reset @${user.username}'s luck to normal?`}
          description={pending === 'exclude'
            ? 'They will never be picked for a spotlight win or favoured in any way — plain chance only, until you include them again. Their wallet and their bets are untouched. The action is logged to audit.'
            : pending === 'include'
              ? 'They become eligible for spotlight wins and comebacks like everyone else. The action is logged to audit.'
              : 'Drops any lined-up spotlight, comeback or resting state and puts them back on plain chance right now. Their 7-day history is kept. The action is logged to audit.'}
          confirmLabel={pending === 'exclude' ? 'Exclude' : pending === 'include' ? 'Include' : 'Reset'}
          cancelLabel="Cancel"
        />
      )}
    </Card>
  );
}

// ─── Wallet adjust card ────────────────────────────────────────────

function WalletAdjustCard({ userId, onChanged, note }: { userId: number; onChanged: () => void; note: (s: string) => void }) {
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [mode, setMode] = useState<'credit' | 'debit'>('credit');
  const [markAsRecharge, setMarkAsRecharge] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setErr(null);
    const amt = Math.round(Number(amount));
    if (!Number.isFinite(amt) || amt <= 0) { setErr('Enter a positive amount'); return; }
    if (!reason.trim()) { setErr('Reason is required'); return; }
    setBusy(true);
    try {
      const r = await adminApi.post<{ referralPayout?: { triggered: boolean; bonus?: number } }>(
        `/admin/users/${userId}/wallet`,
        {
          delta: mode === 'credit' ? amt : -amt,
          reason: reason.trim(),
          markAsRecharge: mode === 'credit' && markAsRecharge,
        }
      );
      let msg = `${mode === 'credit' ? 'Credited' : 'Debited'} ${inr(amt)}`;
      if (r?.referralPayout?.triggered) {
        msg += ` · referrer earned ${inr(r.referralPayout.bonus || 0)} bonus`;
      }
      note(msg);
      setAmount(''); setReason(''); setMarkAsRecharge(false);
      onChanged();
    } catch (e) {
      setErr((e as ApiError).message);
    } finally { setBusy(false); }
  }

  return (
    <Card>
      <h3 className="font-semibold text-[14px] text-neutral-900 dark:text-white inline-flex items-center gap-2 mb-3">
        <Icon name="account_balance_wallet" size={16} className="text-violet-700 dark:text-violet-300" />
        Adjust wallet
      </h3>

      {/* Credit / Debit toggle */}
      <div className="inline-flex rounded-full p-1
                      bg-arena-100/60 dark:bg-arena-800/40
                      ring-1 ring-inset ring-arena-200/50 dark:ring-arena-600/40 mb-3">
        <button type="button" onClick={() => setMode('credit')}
          className={`px-3.5 py-1.5 rounded-full text-[12px] font-bold transition
            ${mode === 'credit'
              ? 'bg-emerald-600 text-white shadow-[0_2px_8px_rgba(16,185,129,0.35)]'
              : 'text-arena-700 hover:text-arena-900 dark:text-neon-200/70 dark:hover:text-white'}`}>
          + Credit
        </button>
        <button type="button" onClick={() => setMode('debit')}
          className={`px-3.5 py-1.5 rounded-full text-[12px] font-bold transition
            ${mode === 'debit'
              ? 'bg-rose-600 text-white shadow-[0_2px_8px_rgba(244,63,94,0.35)]'
              : 'text-arena-700 hover:text-arena-900 dark:text-neon-200/70 dark:hover:text-white'}`}>
          − Debit
        </button>
      </div>

      <form onSubmit={submit} className="space-y-3">
        <Field label="Amount (₹)">
          <input type="number" min={1} value={amount} onChange={(e) => setAmount(e.target.value)}
                 className={inputCls + ' font-mono'} required />
        </Field>
        <Field label="Reason · stored in the ledger">
          <input type="text" value={reason} onChange={(e) => setReason(e.target.value)}
                 placeholder="e.g. deposit reversal, bonus, refund"
                 className={inputCls} required />
        </Field>
        {mode === 'credit' && (
          <label className="flex items-start gap-2.5 cursor-pointer select-none rounded-xl px-3 py-2.5
                            bg-arena-50/40 dark:bg-arena-800/40
                            ring-1 ring-inset ring-arena-200/50 dark:ring-arena-600/40">
            <input type="checkbox" checked={markAsRecharge}
                   onChange={(e) => setMarkAsRecharge(e.target.checked)}
                   className="mt-0.5 w-4 h-4 accent-violet-500" />
            <div>
              <div className="text-[12.5px] font-semibold text-neutral-900 dark:text-white">Mark as recharge</div>
              <div className="text-[11px] text-arena-600 dark:text-neon-200/70 mt-0.5">
                Record this credit as <b>recharge</b> in the ledger. If this user was referred by someone, the referrer auto-gets a bonus.
              </div>
            </div>
          </label>
        )}
        {err && <Banner tone="error">{err}</Banner>}
        <button type="submit" disabled={busy}
                className={`w-full inline-flex items-center justify-center gap-1.5
                            rounded-full px-4 py-2.5 text-[13px] font-bold transition active:scale-95 disabled:opacity-50
                            ${mode === 'credit'
                              ? 'bg-emerald-600 hover:bg-emerald-500 active:bg-emerald-700 text-white shadow-[0_2px_10px_rgba(16,185,129,0.35)]'
                              : 'bg-rose-600 hover:bg-rose-500 active:bg-rose-700 text-white shadow-[0_2px_10px_rgba(244,63,94,0.35)]'}`}>
          {busy ? <Icon name="progress_activity" size={14} className="animate-spin" /> : <Icon name="save" size={14} />}
          {busy ? 'Applying…' : `Apply ${mode}`}
        </button>
      </form>
    </Card>
  );
}

// ─── Password reset card ───────────────────────────────────────────

function PasswordResetCard({ userId, note }: { userId: number; note: (s: string) => void }) {
  const [pwd, setPwd] = useState('');
  const [confirm, setConfirm] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setErr(null);
    if (pwd !== confirm) { setErr('Passwords do not match'); return; }
    if (pwd.length < 8) { setErr('At least 8 characters, with letters and digits'); return; }
    setBusy(true);
    try {
      await adminApi.post(`/admin/users/${userId}/reset-password`, { newPassword: pwd });
      note('Password reset and all sessions revoked');
      setPwd(''); setConfirm('');
    } catch (e) {
      setErr((e as ApiError).message);
    } finally { setBusy(false); }
  }

  return (
    <Card>
      <h3 className="font-semibold text-[14px] text-neutral-900 dark:text-white inline-flex items-center gap-2 mb-1">
        <Icon name="key" size={16} className="text-amber-700 dark:text-amber-300" />
        Reset password
      </h3>
      <p className="text-[11.5px] text-arena-600 dark:text-neon-200/70 mb-3">
        Sets a new password and signs the user out of every device.
      </p>
      <form onSubmit={submit} className="space-y-3">
        <Field label="New password">
          <input type="password" value={pwd} onChange={(e) => setPwd(e.target.value)}
                 className={inputCls} required autoComplete="new-password" />
        </Field>
        <Field label="Confirm">
          <input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)}
                 className={inputCls} required autoComplete="new-password" />
        </Field>
        {err && <Banner tone="error">{err}</Banner>}
        <button type="submit" disabled={busy}
                className="w-full inline-flex items-center justify-center gap-1.5 rounded-full
                           px-4 py-2.5 text-[13px] font-bold
                           bg-amber-600 hover:bg-amber-500 active:bg-amber-700
                           text-white shadow-[0_2px_10px_rgba(245,158,11,0.35)]
                           transition active:scale-95 disabled:opacity-50">
          {busy ? <Icon name="progress_activity" size={14} className="animate-spin" /> : <Icon name="key" size={14} />}
          {busy ? 'Resetting…' : 'Reset password'}
        </button>
      </form>
    </Card>
  );
}

// ─── Ledger card ───────────────────────────────────────────────────

function LedgerCard({ userId }: { userId: number }) {
  const [data, setData] = useState<{ rows: LedgerRow[]; total: number } | null>(null);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const PAGE = 10;

  useEffect(() => {
    setLoading(true);
    adminApi.get<{ rows: LedgerRow[]; total: number }>(`/admin/users/${userId}/ledger?limit=${PAGE}&offset=${page * PAGE}`)
      .then(setData)
      .finally(() => setLoading(false));
  }, [userId, page]);

  const totalPages = data ? Math.max(1, Math.ceil(data.total / PAGE)) : 1;

  return (
    <Card>
      <h3 className="font-semibold text-[14px] text-neutral-900 dark:text-white inline-flex items-center gap-2 mb-3">
        <Icon name="receipt_long" size={16} className="text-arena-600 dark:text-neon-200/70" />
        Wallet ledger
        {data && <span className="ml-auto text-[11px] font-normal text-arena-600 dark:text-neon-200/70">{data.total} entries</span>}
      </h3>

      {loading && (
        <div className="text-[12px] text-arena-600 dark:text-neon-200/70 text-center py-4">Loading…</div>
      )}
      {!loading && data && (
        <>
          <ul className="space-y-1.5 max-h-[420px] overflow-y-auto pr-1">
            {data.rows.length === 0 && (
              <li className="text-[12px] text-arena-600 dark:text-neon-200/70 text-center py-4">
                No transactions yet.
              </li>
            )}
            {data.rows.map((r) => {
              const k = KIND_LABEL[r.kind] || { label: r.kind, icon: 'circle', pos: null };
              const positive = (k.pos ?? (r.amount > 0));
              const gb = gameBadge(r.game);
              return (
                <li key={r.id}
                    className="flex items-center gap-2.5 rounded-xl px-3 py-2.5
                               bg-arena-50/40 dark:bg-arena-800/40
                               ring-1 ring-inset ring-arena-200/50 dark:ring-arena-600/40">
                  <span className={`grid place-items-center w-7 h-7 rounded-full shrink-0
                    ${positive
                      ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-400/15 dark:text-emerald-300'
                      : 'bg-rose-50 text-rose-700 dark:bg-rose-400/15 dark:text-rose-300'}`}>
                    <Icon name={k.icon} size={14} />
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span className="text-[12.5px] font-semibold text-neutral-900 dark:text-white">{k.label}</span>
                      {gb && (
                        <span className={`inline-flex items-center px-1.5 py-0 rounded text-[9.5px] font-bold ${gb.cls}`}>
                          {gb.label}
                        </span>
                      )}
                    </div>
                    {r.note && <div className="text-[11px] text-arena-700 dark:text-neon-200/75 truncate">{r.note}</div>}
                    <div className="text-[10px] text-arena-600 dark:text-neon-200/55">
                      {new Date(r.created_at).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}
                      {r.actor_admin_id && <span> · by admin #{r.actor_admin_id}</span>}
                    </div>
                  </div>
                  <div className={`text-[13px] font-semibold tabular-nums shrink-0
                    ${positive
                      ? 'text-emerald-700 dark:text-emerald-300'
                      : 'text-rose-700 dark:text-rose-300'}`}>
                    {r.amount > 0 ? '+' : ''}{inr(r.amount)}
                  </div>
                </li>
              );
            })}
          </ul>
          {data.total > PAGE && (
            <div className="mt-3 flex items-center justify-between text-[12px]">
              <button disabled={page === 0} onClick={() => setPage((p) => Math.max(0, p - 1))}
                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full
                           bg-arena-100/60 hover:bg-arena-200/70
                           dark:bg-arena-700/40 dark:hover:bg-arena-700/60
                           text-arena-700 dark:text-neon-200/75
                           disabled:opacity-40 transition">
                <Icon name="chevron_left" size={14} /> Prev
              </button>
              <span className="text-arena-600 dark:text-neon-200/70 tabular-nums">{page + 1} / {totalPages}</span>
              <button disabled={page >= totalPages - 1} onClick={() => setPage((p) => p + 1)}
                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full
                           bg-arena-100/60 hover:bg-arena-200/70
                           dark:bg-arena-700/40 dark:hover:bg-arena-700/60
                           text-arena-700 dark:text-neon-200/75
                           disabled:opacity-40 transition">
                Next <Icon name="chevron_right" size={14} />
              </button>
            </div>
          )}
        </>
      )}
    </Card>
  );
}

// ─── Profile editor ────────────────────────────────────────────────

function ProfileEditCard({ user, onSaved, note }: { user: UserFull; onSaved: () => void; note: (s: string) => void }) {
  const [form, setForm] = useState(user);
  // R4 Bug 2 — separate geo bag state, seeded from user record.
  // The legacy free-text address fields were removed from the
  // edit form (server rejects them via .strict() variants); this
  // structured geo bag flows to the same PATCH endpoint via
  // updateProfileZ which now accepts the new geo keys.
  // Phase R6 (2026-05-13): forward the `pincode` 6-digit string the
  // backend now returns alongside the FK so the LocationCapture saved-
  // summary card shows "Pin code: NNNNNN" instantly without a second
  // round-trip to /api/geo/pincode/:id.
  const initialGeo: GeoBag = {
    latitude:    user.latitude    != null ? Number(user.latitude)  : null,
    longitude:   user.longitude   != null ? Number(user.longitude) : null,
    state_id:    user.state_id    ?? null,
    district_id: user.district_id ?? null,
    area_id:     user.area_id     ?? null,
    pincode_id:  user.pincode_id  ?? null,
    pincode:     user.pincode     ?? null,
    nation:      user.nation || 'India',
  };
  const [geo, setGeo] = useState<GeoBag>(initialGeo);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    setForm(user);
    setGeo({
      latitude:    user.latitude    != null ? Number(user.latitude)  : null,
      longitude:   user.longitude   != null ? Number(user.longitude) : null,
      state_id:    user.state_id    ?? null,
      district_id: user.district_id ?? null,
      area_id:     user.area_id     ?? null,
      pincode_id:  user.pincode_id  ?? null,
      pincode:     user.pincode     ?? null,
      nation:      user.nation || 'India',
    });
  }, [user]);

  function set<K extends keyof UserFull>(k: K, v: UserFull[K]) { setForm({ ...form, [k]: v }); }

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true); setErr(null);
    const patch: Record<string, unknown> = {};
    // Identity / profile fields — only forward what changed.
    for (const k of ['username','email','phone','first_name','last_name','birthdate','gender'] as const) {
      if ((form[k] || null) !== (user[k] || null)) {
        patch[k] = form[k];
      }
    }
    // R4 Bug 2 — geo: forward whichever keys differ from the
    // server-side baseline. Sending `null` is valid (it clears the
    // column); auto-stamps geo_updated_at on the model side.
    const geoKeys: (keyof GeoBag)[] = ['latitude','longitude','state_id','district_id','area_id','pincode_id','nation'];
    let geoTouched = false;
    for (const k of geoKeys) {
      const before = (initialGeo[k] ?? null) as unknown;
      const now    = (geo[k]        ?? null) as unknown;
      if (before !== now) {
        patch[k] = geo[k];
        geoTouched = true;
      }
    }
    // If geo moved at all, ensure nation is included (preserves
    // 'India' default even when the user didn't touch it).
    if (geoTouched && patch.nation === undefined && geo.nation) {
      patch.nation = geo.nation;
    }
    if (!Object.keys(patch).length) { setBusy(false); note('Nothing to save'); return; }
    try {
      await adminApi.patch(`/admin/users/${user.id}`, patch);
      note(`Saved ${Object.keys(patch).length} field(s)`);
      onSaved();
    } catch (e) {
      setErr((e as ApiError).message);
    } finally { setBusy(false); }
  }

  const TextInput = (label: string, field: keyof UserFull, type = 'text') => (
    <Field label={label}>
      <input type={type}
             value={(form[field] as string | null) ?? ''}
             onChange={(e) => set(field, (e.target.value || null) as never)}
             className={inputCls} />
    </Field>
  );

  return (
    <Card>
      <h3 className="font-semibold text-[14px] text-neutral-900 dark:text-white inline-flex items-center gap-2 mb-4">
        <Icon name="edit" size={16} className="text-arena-600 dark:text-neon-200/70" />
        Edit profile
      </h3>
      <form onSubmit={submit}>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {TextInput('Username', 'username')}
          {TextInput('Email', 'email', 'email')}
          {TextInput('Phone', 'phone')}
          {TextInput('First name', 'first_name')}
          {TextInput('Last name', 'last_name')}
          {TextInput('Date of birth', 'birthdate', 'date')}
          <Field label="Gender">
            <select value={form.gender || ''} onChange={(e) => set('gender', (e.target.value || null) as never)}
                    className={inputCls}>
              <option value="">—</option>
              <option value="male">Male</option>
              <option value="female">Female</option>
              <option value="other">Other</option>
              <option value="prefer_not">Prefer not to say</option>
            </select>
          </Field>
        </div>

        {/* R4 Bug 2 — Location editor. Replaces the 7 free-text
            address inputs (legacy_* on the server; rejected by Zod
            on writes). Admin uses pincode lookup or manual cascade
            to correct the player's structured geo. GPS is HIDDEN
            (creator-mode) — admin would otherwise capture admin's
            own office coordinates and clobber the player's row. */}
        <div className="mt-4 pt-4 border-t border-arena-200/40 dark:border-arena-700/40">
          <div className="text-[11px] font-bold uppercase tracking-wider text-arena-600 dark:text-neon-200/70 mb-2">
            Location {user.geo_updated_at && (
              <span className="font-normal lowercase opacity-60">
                · last updated {new Date(user.geo_updated_at).toLocaleDateString()}
              </span>
            )}
          </div>
          <LocationCapture
            key={user.geo_updated_at ?? 'new'}
        value={geo}
            onChange={setGeo}
            mode="child"                     // admin editing someone else (no GPS, pin-code only)
            disabled={busy}
          />
        </div>
        {err && <div className="mt-3"><Banner tone="error">{err}</Banner></div>}
        <div className="mt-4 flex items-center gap-2 flex-wrap">
          <button type="submit" disabled={busy}
                  className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-[12.5px] font-bold
                             bg-violet-600 hover:bg-violet-500 active:bg-violet-700
                             text-white shadow-[0_2px_10px_rgba(139,92,246,0.35)]
                             transition active:scale-95 disabled:opacity-50">
            {busy ? <Icon name="progress_activity" size={14} className="animate-spin" /> : <Icon name="save" size={14} />}
            {busy ? 'Saving…' : 'Save changes'}
          </button>
          <button type="button" onClick={() => setForm(user)}
                  className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-[12.5px] font-bold
                             bg-arena-100/60 hover:bg-arena-200/70
                             dark:bg-arena-700/40 dark:hover:bg-arena-700/60
                             text-arena-700 dark:text-neon-200/75
                             ring-1 ring-inset ring-arena-200/50 dark:ring-arena-600/40
                             transition active:scale-95">
            <Icon name="undo" size={13} /> Reset
          </button>
        </div>
      </form>
    </Card>
  );
}

// ─── Ownership card + picker ───────────────────────────────────────

// OwnershipCard — rebuilt 2026-05-16 for mobile-first.
//
// Previously: a horizontal flex row that crammed avatar + identity +
// TWO action buttons into a single line. On phones the buttons
// hogged ~200px while the identity column truncated to "@..." with
// the description wrapping into a 3-line vertical strip. Ugly.
//
// Now: a self-contained Card with a stacked layout —
//   • Identity row at the top (avatar + name + description), full
//     width, never truncates.
//   • Action buttons on a second row, side-by-side on sm+ but a
//     2-col grid on mobile so each button gets equal half-width
//     instead of squashing into a corner.
//
// The card also owns its own <Card> wrapper + h3 now, so the caller
// just renders <OwnershipCard ... /> without a wrapping shell. Drops
// the redundant double-heading the previous layout produced.
function OwnershipCard({ user, reload, note }: { user: UserFull; reload: () => void; note: (s: string) => void }) {
  const { admin } = useAdminAuth();
  const [picking, setPicking] = useState(false);
  // Phase R5 — separate state for the UAID-keyed transfer modal so
  // the existing list-of-agents picker stays available for admin
  // muscle memory. Both buttons live side-by-side on sm+; admin
  // picks whichever is faster for the situation.
  const [transferring, setTransferring] = useState(false);
  if (admin?.role !== 'admin') return null;
  const owned = user.agent_id != null;

  return (
    <>
      <Card>
        <h3 className="font-semibold text-[14px] text-neutral-900 dark:text-white inline-flex items-center gap-2 mb-3">
          <Icon name="support_agent" size={16} className="text-emerald-500" />
          Owner agent
        </h3>

        {/* Identity row — avatar/icon + name + description.
            Full-width so long usernames don't truncate. */}
        <div className="flex items-start gap-3 rounded-xl px-3 py-2.5
                        bg-arena-50/50 dark:bg-arena-800/40
                        ring-1 ring-inset ring-arena-200/50 dark:ring-arena-600/40">
          {owned ? (
            <AdminAvatar url={user.owner_avatar_url} username={user.owner_username || 'Agent'} size="md" />
          ) : (
            <div className="grid place-items-center w-10 h-10 rounded-xl
                            bg-violet-50 text-violet-700 ring-1 ring-inset ring-violet-200/60
                            dark:bg-violet-400/15 dark:text-violet-300 dark:ring-violet-400/30 shrink-0">
              <Icon name="shield" size={20} filled />
            </div>
          )}
          <div className="min-w-0 flex-1">
            {owned ? (
              <>
                <div className="text-[14px] font-bold text-emerald-700 dark:text-emerald-300 truncate leading-tight">
                  @{user.owner_username || `agent #${user.agent_id}`}
                </div>
                <div className="text-[11px] text-arena-600 dark:text-neon-200/65 leading-snug mt-0.5">
                  Earns commissions on this player's recharges + matches.
                </div>
              </>
            ) : (
              <>
                <div className="text-[14px] font-bold text-neutral-900 dark:text-white leading-tight">
                  Platform-direct
                </div>
                <div className="text-[11px] text-arena-600 dark:text-neon-200/65 leading-snug mt-0.5">
                  No agent owns this player. Admin manages directly.
                </div>
              </>
            )}
          </div>
        </div>

        {/* Action row — 2-col grid on mobile (each button gets half
            the width), inline flex on sm+ where there's room. The
            "Change agent" path opens the browse-and-pick modal; the
            "Transfer by UAID" path opens the paste-an-ID modal. Same
            outcome, two muscle-memory paths for admin. */}
        <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
          <button
            onClick={() => setPicking(true)}
            className="inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-full text-[12.5px] font-bold
                       bg-emerald-600 hover:bg-emerald-500 active:bg-emerald-700
                       text-white shadow-[0_2px_10px_rgba(16,185,129,0.35)]
                       transition active:scale-95">
            <Icon name="swap_horiz" size={14} /> Change agent
          </button>
          <button
            onClick={() => setTransferring(true)}
            className="inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-full text-[12.5px] font-bold
                       bg-cyan-600 hover:bg-cyan-500 active:bg-cyan-700
                       text-white shadow-[0_2px_10px_rgba(6,182,212,0.4)]
                       transition active:scale-95">
            <Icon name="badge" size={14} /> Transfer by UAID
          </button>
        </div>
      </Card>

      {picking && (
        <AgentReassignSheet
          subject={{ kind: 'player', label: user.username, currentParentId: user.agent_id ?? null }}
          detach={{ label: 'Detach to platform', description: 'Remove agent ownership. Admin manages directly.', icon: 'shield' }}
          onClose={() => setPicking(false)}
          onAssign={async (newParentId, _reason, pickedAgent) => {
            await adminApi.post(`/admin/users/${user.id}/owner`, { agent_id: newParentId });
            setPicking(false);
            note(`Ownership → ${pickedAgent ? `@${pickedAgent.username}` : 'Platform-direct'}`);
            reload();
          }}
        />
      )}
      {transferring && (
        <PlayerTransferModal
          player={{ id: user.id, username: user.username }}
          onClose={() => setTransferring(false)}
          onTransferred={() => { note('Player transferred via UAID'); reload(); }}
        />
      )}
    </>
  );
}

// ─── Shared bits ───────────────────────────────────────────────────

// 2026-05-20 — was a local clone of INPUT_CLS that drifted out of
// sync with the canonical token. Aliased so the dark-mode fill+ring
// fix in components/fieldStyles.ts propagates here automatically.
const inputCls = INPUT_CLS;

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="block text-[10.5px] uppercase tracking-wider text-arena-600 dark:text-neon-200/70 mb-1 font-semibold">{label}</span>
      {children}
    </label>
  );
}

// ═══════════════════════════════════════════════════════════════════
// Entity Redesign Step 3 (2026-05-16) — new shells
// ═══════════════════════════════════════════════════════════════════

// ─── Pinned identity header ───────────────────────────────────────
//
// Sticky on scroll. Always-visible "who am I editing?" context so
// admin doesn't lose their orientation deep in the audit feed or
// ledger. Mirrors the high-density list-card layout, with the
// addition of an explicit kebab in the top-right corner that opens
// the full Tier-1 action sheet.
function PinnedHeader({
  user, canEditAvatar, onPhotoEdit, onOpenActions,
}: {
  user: UserFull;
  canEditAvatar: boolean;
  onPhotoEdit: () => void;
  onOpenActions: () => void;
}) {
  const displayName = (user.first_name || user.last_name)
    ? `${user.first_name || ''} ${user.last_name || ''}`.trim()
    : '(no name set)';
  const emailOk    = !!user.email_verified_at;
  const phoneOk    = !!user.phone_verified_at;
  const telegramOk = !!user.telegram_verified_at;
  const lockedOut  = !!user.locked_until && new Date(user.locked_until) > new Date();
  const isTest     = !!user.is_test;

  return (
    <motion.div
      initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }}
      className="sticky top-0 z-30 -mx-2 lg:-mx-4 px-2 lg:px-4 py-2
                 bg-neutral-50/85 dark:bg-arena-900/85 backdrop-blur
                 supports-backdrop-blur:bg-neutral-50/70
                 supports-backdrop-blur:dark:bg-arena-900/70">
      <div className="rounded-2xl p-3 sm:p-4
                      bg-white dark:bg-arena-700/40
                      ring-1 ring-inset ring-arena-200/55 dark:ring-arena-600/40
                      shadow-[0_1px_3px_rgba(0,0,0,0.04)] dark:shadow-none
                      relative">
        {/* Kebab — opens EntityActionSheet with Tier-1 verbs */}
        <button
          onClick={onOpenActions}
          className="absolute top-2 right-2 grid place-items-center w-9 h-9 rounded-full
                     text-arena-500 dark:text-neon-200/65 hover:bg-arena-100/60 dark:hover:bg-arena-700/60
                     active:bg-arena-200/60 dark:active:bg-arena-700"
          aria-label="More actions"
        >
          <Icon name="more_horiz" size={18} />
        </button>

        <div className="flex items-start gap-3 pr-10">
          <div className="relative shrink-0">
            <AdminAvatar url={user.profile_image_url} username={user.username} size="lg" />
            {canEditAvatar && (
              <button
                onClick={onPhotoEdit}
                title="Change photo"
                className="absolute -bottom-1 -right-1 grid place-items-center w-7 h-7 rounded-full
                           bg-violet-600 hover:bg-violet-500 active:bg-violet-700
                           text-white shadow-md
                           focus:outline-none focus:ring-2 focus:ring-violet-400/60 transition active:scale-95"
              >
                <Icon name="photo_camera" size={14} />
              </button>
            )}
          </div>

          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1.5 flex-wrap">
              <h2 className="text-[15px] sm:text-[17px] font-display font-extrabold text-neutral-900 dark:text-white truncate leading-tight">
                {displayName}
              </h2>
              <span className="shrink-0 px-1.5 py-0.5 rounded-full text-[9.5px] font-extrabold uppercase tracking-[0.12em] whitespace-nowrap
                               bg-cyan-600 text-white dark:bg-cyan-500 shadow-[0_1px_3px_rgba(14,165,233,0.4)]">
                Player
              </span>
              <span className={`shrink-0 inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[10px] font-bold capitalize ${STATUS_PILL[user.status]}`}>
                <span className="w-1 h-1 rounded-full bg-current" />
                {user.status}
              </span>
            </div>
            {/* Phase C4 — Identity Stack (admin → player, full visibility).
                Display name is the H2 above; line 2 = @username, line 3
                = phone. Admin always has full identity access. */}
            <div className="text-[11.5px] text-arena-600 dark:text-neon-200/70 truncate mt-0.5 font-mono">
              @{user.username} <span className="text-arena-400 dark:text-neon-200/70">·</span> #{user.id}
            </div>
            {user.phone && (
              <PhoneContact phone={user.phone} name={user.username} iconSize={11}
                className="text-[11.5px] text-arena-700 dark:text-neon-200/80 truncate tabular-nums inline-flex items-center gap-1 hover:text-arena-900 dark:hover:text-neon-200" />
            )}

            {/* Chip row — verification dots + flags */}
            <div className="mt-2 flex items-center gap-1.5 flex-wrap text-[10px]">
              <VerDot icon="mail" ok={emailOk} label="email" />
              <VerDot icon="call" ok={phoneOk} label="phone" />
              <VerDot icon="send" ok={telegramOk} label="telegram" />
              {isTest && (
                <span className="inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 font-bold
                                 bg-amber-50 text-amber-700 ring-1 ring-inset ring-amber-200/70
                                 dark:bg-amber-400/15 dark:text-amber-300 dark:ring-amber-400/30">
                  <Icon name="science" size={9} filled /> test
                </span>
              )}
              {lockedOut && (
                <span className="inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 font-bold
                                 bg-orange-50 text-orange-700 ring-1 ring-inset ring-orange-200/60
                                 dark:bg-orange-400/15 dark:text-orange-300 dark:ring-orange-400/30">
                  <Icon name="lock_clock" size={9} filled /> locked out
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Money + ownership strip — flex row, not a 2-col grid that
            would compress to nothing on phones */}
        <div className="mt-3 pt-3 border-t border-arena-200/40 dark:border-arena-700/40
                        flex items-baseline justify-between gap-2">
          <div className="min-w-0">
            <div className="text-[15px] sm:text-[17px] font-display font-extrabold text-cyan-700 dark:text-cyan-300 tabular-nums leading-none">
              {inr(user.wallet_balance)}
            </div>
            <div className="text-[10px] text-arena-500 dark:text-neon-200/55 mt-0.5 leading-none">wallet</div>
          </div>
          <div className="text-right min-w-0 max-w-[55%]">
            <div className="text-[11.5px] font-semibold text-neutral-900 dark:text-white truncate leading-tight">
              {user.owner_username
                ? <>under <span className="text-arena-700 dark:text-neon-200/85">@{user.owner_username}</span></>
                : <span className="italic text-arena-400 dark:text-neon-200/45">platform direct</span>}
            </div>
            <div className="text-[10px] text-arena-500 dark:text-neon-200/55 leading-tight mt-0.5">
              joined {new Date(user.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: '2-digit' })}
            </div>
            <div className="text-[10px] text-arena-500 dark:text-neon-200/55 leading-tight mt-0.5">
              last login {user.last_login_at
                ? new Date(user.last_login_at).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })
                : 'never'}
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

function VerDot({ icon, ok, label }: { icon: string; ok: boolean; label: string }) {
  return (
    <span title={`${label}: ${ok ? 'verified' : 'not verified'}`}
      className={`inline-flex items-center justify-center w-4 h-4 rounded-full
        ${ok
          ? 'bg-emerald-50 text-emerald-700 ring-1 ring-inset ring-emerald-200/60 dark:bg-emerald-400/15 dark:text-emerald-300 dark:ring-emerald-400/30'
          : 'bg-arena-50 text-arena-400 ring-1 ring-inset ring-arena-200/50 dark:bg-arena-700/40 dark:text-neon-200/30 dark:ring-arena-600/40'}`}>
      <Icon name={icon} size={9} filled={ok} />
    </span>
  );
}

// ─── Overview tab ─────────────────────────────────────────────────
//
// Single-fetch dashboard: hits /admin/users/:id/aggregate once and
// renders four money tiles + four activity tiles + a referrals card.
// Mobile-first: 2-column tile grid, stacks tighter under sm.
function OverviewTab({ uid, user, onAdjust }: { uid: number; user: UserFull; onAdjust: () => void }) {
  const [data, setData] = useState<AggregateResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true); setErr(null);
      try {
        const r = await adminApi.get<AggregateResponse>(`/admin/users/${uid}/aggregate`);
        if (!cancelled) setData(r);
      } catch (e) {
        if (!cancelled) setErr((e as ApiError).message || 'Failed to load overview');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [uid]);

  return (
    <div className="space-y-4">
      {/* Money tiles */}
      <div>
        <SectionTitle icon="payments" tone="emerald">Money</SectionTitle>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3">
          <StatTile label="Wallet" value={data?.money.wallet_balance ?? user.wallet_balance} tone="cyan" loading={loading} primary />
          <StatTile label="Lifetime recharge" value={data?.money.lifetime_recharge ?? 0} tone="emerald" loading={loading} />
          <StatTile label="Lifetime wager" value={data?.money.lifetime_wager ?? 0} tone="violet" loading={loading} />
          <StatTile label="Lifetime winnings" value={data?.money.lifetime_winnings ?? 0} tone="amber" loading={loading} />
        </div>
      </div>

      {/* Activity tiles — 4 of them so the 2-col mobile grid lays
          out cleanly (2 + 2). On sm+ the row expands to 4-up. */}
      <div>
        <SectionTitle icon="bolt" tone="cyan">Activity</SectionTitle>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3">
          <RelTile label="Last match"
                   value={data?.activity.last_match_at}
                   suffix={data?.activity.last_match_game} loading={loading} />
          <CountTile label="Active sessions" value={data?.activity.session_count ?? 0} icon="devices" loading={loading} />
          <CountTile label="Audit events" value={data?.activity.audit_count ?? 0} icon="receipt_long" loading={loading} />
          <CountTile label="Failed logins" value={user.failed_login_attempts} icon="block" loading={loading} />
        </div>
      </div>

      {/* Referrals */}
      <div>
        <SectionTitle icon="hub" tone="violet">Referrals</SectionTitle>
        <ReferralsSummary stats={data?.referrals} loading={loading} />
      </div>

      {/* Inline CTA — same stack-on-mobile pattern as the Wallet
          tab's Adjust card so helper copy stays readable at 414px. */}
      <Card>
        <div className="flex items-start gap-3 mb-3">
          <span className="grid place-items-center w-9 h-9 rounded-xl shrink-0
                           bg-violet-50 text-violet-700 ring-1 ring-inset ring-violet-200/60
                           dark:bg-violet-400/15 dark:text-violet-300 dark:ring-violet-400/30">
            <Icon name="tune" size={18} filled />
          </span>
          <div className="min-w-0 flex-1">
            <div className="text-[13px] font-bold text-neutral-900 dark:text-white leading-tight">
              Need to credit or debit?
            </div>
            <div className="text-[11.5px] text-arena-600 dark:text-neon-200/70 mt-1 leading-snug">
              Open the rich direct-adjust modal here, or use the kebab (•••)
              in the top-right.
            </div>
          </div>
        </div>
        <button
          onClick={onAdjust}
          className="w-full sm:w-auto inline-flex items-center justify-center gap-1.5 h-9 px-4 rounded-full
                     bg-violet-600 hover:bg-violet-500 text-white text-[12.5px] font-bold
                     shadow-[0_2px_10px_rgba(139,92,246,0.45)] transition active:scale-95">
          <Icon name="account_balance_wallet" size={14} /> Open direct credit / debit
        </button>
      </Card>

      {err && <Banner tone="error">{err}</Banner>}
    </div>
  );
}

function SectionTitle({ icon, tone, children }: { icon: string; tone: 'emerald' | 'cyan' | 'violet'; children: React.ReactNode }) {
  const tones = {
    emerald: 'text-emerald-700 dark:text-emerald-300',
    cyan:    'text-cyan-700 dark:text-cyan-300',
    violet:  'text-violet-700 dark:text-violet-300',
  } as const;
  return (
    <h3 className={`text-[11px] font-bold uppercase tracking-wider mb-2 inline-flex items-center gap-1.5 ${tones[tone]}`}>
      <Icon name={icon} size={13} filled />
      {children}
    </h3>
  );
}

function StatTile({
  label, value, tone, loading, primary = false,
}: {
  label: string; value: number; tone: 'cyan' | 'emerald' | 'violet' | 'amber';
  loading: boolean; primary?: boolean;
}) {
  const toneCls = {
    cyan:    'text-cyan-700 dark:text-cyan-300',
    emerald: 'text-emerald-700 dark:text-emerald-300',
    violet:  'text-violet-700 dark:text-violet-300',
    amber:   'text-amber-700 dark:text-amber-300',
  }[tone];
  return (
    <div className={`rounded-2xl p-3 bg-white dark:bg-arena-700/40
                     ring-1 ring-inset ring-arena-200/55 dark:ring-arena-600/40
                     ${primary ? 'sm:row-span-1' : ''}`}>
      <div className="text-[9.5px] uppercase tracking-wider font-bold text-arena-600 dark:text-neon-200/55 leading-none">
        {label}
      </div>
      <div className={`mt-1.5 font-display font-extrabold tabular-nums leading-none ${toneCls} ${primary ? 'text-[20px] sm:text-[24px]' : 'text-[16px] sm:text-[20px]'}`}>
        {loading ? '—' : inr(value)}
      </div>
    </div>
  );
}

function CountTile({ label, value, icon, loading }: { label: string; value: number; icon: string; loading: boolean }) {
  return (
    <div className="rounded-2xl p-3 bg-white dark:bg-arena-700/40
                    ring-1 ring-inset ring-arena-200/55 dark:ring-arena-600/40">
      <div className="text-[9.5px] uppercase tracking-wider font-bold text-arena-600 dark:text-neon-200/55 leading-none inline-flex items-center gap-1">
        <Icon name={icon} size={10} /> {label}
      </div>
      <div className="mt-1.5 text-[16px] sm:text-[20px] font-display font-extrabold text-neutral-900 dark:text-white tabular-nums leading-none">
        {loading ? '—' : value.toLocaleString('en-IN')}
      </div>
    </div>
  );
}

function RelTile({ label, value, suffix, loading }: { label: string; value: string | null | undefined; suffix?: string | null; loading: boolean }) {
  const rel = (() => {
    if (loading) return '—';
    if (!value) return 'never';
    const ms = Date.now() - new Date(value).getTime();
    if (ms < 60_000) return 'just now';
    const m = Math.floor(ms / 60_000);
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    const d = Math.floor(h / 24);
    if (d < 30) return `${d}d ago`;
    return `${Math.floor(d / 30)}mo ago`;
  })();
  return (
    <div className="rounded-2xl p-3 bg-white dark:bg-arena-700/40
                    ring-1 ring-inset ring-arena-200/55 dark:ring-arena-600/40">
      <div className="text-[9.5px] uppercase tracking-wider font-bold text-arena-600 dark:text-neon-200/55 leading-none inline-flex items-center gap-1">
        <Icon name="schedule" size={10} /> {label}
      </div>
      <div className="mt-1.5 text-[16px] sm:text-[20px] font-display font-extrabold text-neutral-900 dark:text-white tabular-nums leading-none"
           title={value || ''}>
        {rel}
      </div>
      {suffix && <div className="text-[10px] text-arena-500 dark:text-neon-200/55 mt-1 uppercase tracking-wider">{suffix}</div>}
    </div>
  );
}

function ReferralsSummary({ stats, loading }: { stats?: AggregateResponse['referrals']; loading: boolean }) {
  if (loading || !stats) {
    return (
      <Card>
        <div className="text-[12px] text-arena-600 dark:text-neon-200/70 italic">Loading referrals…</div>
      </Card>
    );
  }
  if (stats.total === 0) {
    return (
      <Card>
        <div className="text-[12px] text-arena-600 dark:text-neon-200/70 italic">
          No referrals on this account yet.
        </div>
      </Card>
    );
  }
  return (
    <Card>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3 text-[12px]">
        <RefStat label="Total" value={stats.total} tone="neutral" />
        <RefStat label="Pending" value={stats.pending} tone="amber" />
        <RefStat label="Credited" value={stats.credited} tone="emerald" />
        <RefStat label="Rejected" value={stats.rejected} tone="rose" />
      </div>
      <div className="mt-3 pt-3 border-t border-arena-200/40 dark:border-arena-700/40 flex items-center justify-between">
        <div className="text-[11px] text-arena-600 dark:text-neon-200/65">Total earned from referrals</div>
        <div className="text-[14px] font-display font-extrabold text-emerald-700 dark:text-emerald-300 tabular-nums">
          {inr(stats.total_earned)}
        </div>
      </div>
    </Card>
  );
}

function RefStat({ label, value, tone }: { label: string; value: number; tone: 'neutral' | 'amber' | 'emerald' | 'rose' }) {
  const cls = {
    neutral: 'text-neutral-900 dark:text-white',
    amber:   'text-amber-700 dark:text-amber-300',
    emerald: 'text-emerald-700 dark:text-emerald-300',
    rose:    'text-rose-700 dark:text-rose-300',
  }[tone];
  return (
    <div>
      <div className="text-[9.5px] uppercase tracking-wider font-bold text-arena-600 dark:text-neon-200/55">{label}</div>
      <div className={`text-[16px] font-display font-extrabold tabular-nums leading-tight ${cls}`}>{value}</div>
    </div>
  );
}

// ─── Recent matches card ─────────────────────────────────────────
//
// Last N matches across all 5 games, mixed by time. Powered by
// /admin/users/:id/matches which UNIONs the 5 *_sessions tables.
function RecentMatchesCard({ uid }: { uid: number }) {
  const [rows, setRows] = useState<Array<{ game: string; session_id: number; pot: number; status: string; outcome: string; bet_paid: number; created_at: string; is_bot: number }>>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true); setErr(null);
      try {
        const r = await adminApi.get<{ rows: typeof rows }>(`/admin/users/${uid}/matches?limit=20`);
        if (!cancelled) setRows(r.rows || []);
      } catch (e) {
        if (!cancelled) setErr((e as ApiError).message || 'Failed to load matches');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [uid]);

  const outcomeBadge = (o: string) => {
    const m: Record<string, string> = {
      win:       'bg-emerald-50 text-emerald-700 ring-1 ring-inset ring-emerald-200/60 dark:bg-emerald-400/15 dark:text-emerald-300 dark:ring-emerald-400/30',
      loss:      'bg-rose-50 text-rose-700 ring-1 ring-inset ring-rose-200/60 dark:bg-rose-400/15 dark:text-rose-300 dark:ring-rose-400/30',
      draw:      'bg-arena-50 text-arena-700 ring-1 ring-inset ring-arena-200/60 dark:bg-arena-700/40 dark:text-neon-200/75 dark:ring-arena-600/40',
      cancelled: 'bg-amber-50 text-amber-700 ring-1 ring-inset ring-amber-200/60 dark:bg-amber-400/15 dark:text-amber-300 dark:ring-amber-400/30',
    };
    return m[o] || m.draw;
  };

  return (
    <Card>
      <h3 className="font-semibold text-[14px] text-neutral-900 dark:text-white inline-flex items-center gap-2 mb-3">
        <Icon name="sports_esports" size={16} className="text-cyan-500" />
        Recent matches
      </h3>
      {err && <div className="text-[11.5px] text-rose-700 dark:text-rose-300 mb-2">{err}</div>}
      {loading && (
        <div className="text-[12px] text-arena-600 dark:text-neon-200/70 italic">
          <Icon name="progress_activity" size={12} className="animate-spin inline align-text-bottom mr-1" />
          Loading…
        </div>
      )}
      {!loading && rows.length === 0 && (
        <div className="text-[12px] text-arena-600 dark:text-neon-200/55 italic">No matches played yet.</div>
      )}
      {!loading && rows.length > 0 && (
        <div className="space-y-1.5 max-h-[420px] overflow-y-auto">
          {rows.map((r) => {
            const b = gameBadge(r.game);
            return (
              <div key={`${r.game}:${r.session_id}`}
                   className="flex items-center gap-2 px-2 py-1.5 rounded-xl
                              bg-arena-50/40 dark:bg-arena-800/30
                              ring-1 ring-inset ring-arena-200/40 dark:ring-arena-600/30 text-[11.5px]">
                {b && (
                  <span className={`shrink-0 px-1.5 py-0.5 rounded text-[9.5px] font-bold uppercase tracking-wider ${b.cls}`}>
                    {b.label}
                  </span>
                )}
                <span className={`shrink-0 px-1.5 py-0.5 rounded-full text-[9.5px] font-semibold uppercase tracking-wider ${outcomeBadge(r.outcome)}`}>
                  {r.outcome}
                </span>
                <div className="min-w-0 flex-1 text-arena-600 dark:text-neon-200/70 truncate">
                  bet {inr(r.bet_paid)} · pot {inr(r.pot)}
                  {r.is_bot ? <span className="ml-1 text-arena-400 dark:text-neon-200/70">(bot)</span> : null}
                </div>
                <span className="shrink-0 text-[10.5px] text-arena-500 dark:text-neon-200/55 tabular-nums">
                  {new Date(r.created_at).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
}

// ─── Sessions card ─────────────────────────────────────────────────
//
// Live + recent refresh tokens. Active row count surfaced in the
// header. Admin can use the kebab to force-logout (which revokes
// all tokens at once).
function SessionsCard({ uid }: { uid: number }) {
  const [rows, setRows] = useState<Array<{ id: number; user_agent: string | null; ip: string | null; expires_at: string; revoked_at: string | null; created_at: string; is_active: 0 | 1 }>>([]);
  const [total, setTotal] = useState(0);
  const [active, setActive] = useState(0);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true); setErr(null);
      try {
        const r = await adminApi.get<{ rows: typeof rows; total: number; active: number }>(`/admin/users/${uid}/sessions?limit=25`);
        if (!cancelled) {
          setRows(r.rows || []);
          setTotal(r.total);
          setActive(r.active);
        }
      } catch (e) {
        if (!cancelled) setErr((e as ApiError).message || 'Failed to load sessions');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [uid]);

  return (
    <Card>
      <div className="flex items-start justify-between gap-2 mb-3">
        <h3 className="font-semibold text-[14px] text-neutral-900 dark:text-white inline-flex items-center gap-2">
          <Icon name="devices" size={16} className="text-violet-500" />
          Sessions
        </h3>
        <span className="shrink-0 text-[11px] font-semibold text-emerald-700 dark:text-emerald-300">
          {active} active / {total} total
        </span>
      </div>
      {err && <div className="text-[11.5px] text-rose-700 dark:text-rose-300 mb-2">{err}</div>}
      {loading && <div className="text-[12px] text-arena-600 dark:text-neon-200/70 italic">Loading…</div>}
      {!loading && rows.length === 0 && (
        <div className="text-[12px] text-arena-600 dark:text-neon-200/55 italic">No sessions on record.</div>
      )}
      {!loading && rows.length > 0 && (
        <div className="space-y-1.5 max-h-[420px] overflow-y-auto">
          {rows.map((r) => {
            const isActive = !!r.is_active;
            return (
              <div key={r.id}
                   className={`flex items-center gap-2 px-2 py-1.5 rounded-xl text-[11.5px]
                               ring-1 ring-inset
                               ${isActive
                                 ? 'bg-emerald-50/50 dark:bg-emerald-400/10 ring-emerald-200/60 dark:ring-emerald-400/25'
                                 : 'bg-arena-50/40 dark:bg-arena-800/30 ring-arena-200/40 dark:ring-arena-600/30'}`}>
                <span className={`shrink-0 w-2 h-2 rounded-full ${isActive ? 'bg-emerald-500' : 'bg-arena-400 dark:bg-neon-200/30'}`} />
                <div className="min-w-0 flex-1">
                  <div className="text-arena-800 dark:text-neon-200/85 truncate">
                    {r.user_agent ? r.user_agent.split(' ').slice(0, 4).join(' ') : 'Unknown UA'}
                  </div>
                  <div className="text-[10px] text-arena-500 dark:text-neon-200/55 tabular-nums truncate">
                    {r.ip || '—'} · {new Date(r.created_at).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                  </div>
                </div>
                {r.revoked_at && (
                  <span className="shrink-0 text-[9.5px] font-bold uppercase tracking-wider text-rose-700 dark:text-rose-300">revoked</span>
                )}
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
}

// ─── Audit card ────────────────────────────────────────────────────
//
// audit_log feed scoped to this player. Surfaces every admin action
// touching this row (status changes, force-verify, wallet adjusts,
// reassigns, deletes, etc.) so the operator can answer "who did
// what to this account?" without leaving the page.
function AuditCard({ uid }: { uid: number }) {
  const [rows, setRows] = useState<Array<{ id: number; event: string; actor_username: string | null; actor_role: string | null; meta: any; ip: string | null; created_at: string }>>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true); setErr(null);
      try {
        const r = await adminApi.get<{ rows: typeof rows }>(`/admin/users/${uid}/audit?limit=50`);
        if (!cancelled) setRows(r.rows || []);
      } catch (e) {
        if (!cancelled) setErr((e as ApiError).message || 'Failed to load audit log');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [uid]);

  // Prettify event names: 'admin.user_force_verify_email' → 'force verify email'
  const pretty = (ev: string) => ev.replace(/^(admin|agent|user)\./, '').replace(/_/g, ' ');

  return (
    <Card>
      <h3 className="font-semibold text-[14px] text-neutral-900 dark:text-white inline-flex items-center gap-2 mb-3">
        <Icon name="receipt_long" size={16} className="text-amber-500" />
        Audit log
      </h3>
      {err && <div className="text-[11.5px] text-rose-700 dark:text-rose-300 mb-2">{err}</div>}
      {loading && <div className="text-[12px] text-arena-600 dark:text-neon-200/70 italic">Loading…</div>}
      {!loading && rows.length === 0 && (
        <div className="text-[12px] text-arena-600 dark:text-neon-200/55 italic">No audit entries.</div>
      )}
      {!loading && rows.length > 0 && (
        <div className="space-y-1.5 max-h-[460px] overflow-y-auto">
          {rows.map((r) => (
            <div key={r.id}
                 className="px-2 py-1.5 rounded-xl
                            bg-arena-50/40 dark:bg-arena-800/30
                            ring-1 ring-inset ring-arena-200/40 dark:ring-arena-600/30">
              <div className="flex items-center gap-2 text-[11.5px]">
                <span className="font-mono text-[10px] text-arena-500 dark:text-neon-200/45 tabular-nums">#{r.id}</span>
                <span className="font-semibold text-neutral-900 dark:text-white truncate">{pretty(r.event)}</span>
                <span className="ml-auto shrink-0 text-[10px] text-arena-500 dark:text-neon-200/55 tabular-nums">
                  {new Date(r.created_at).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                </span>
              </div>
              <div className="text-[10.5px] text-arena-600 dark:text-neon-200/65 mt-0.5 truncate">
                by {r.actor_username ? <span className="font-semibold text-arena-700 dark:text-neon-200/85">@{r.actor_username}</span> : <span className="italic text-arena-400">system</span>}
                {r.actor_role && <span className="text-arena-400 dark:text-neon-200/70"> ({r.actor_role})</span>}
                {r.ip && <span className="text-arena-400 dark:text-neon-200/70 ml-1">· {r.ip}</span>}
              </div>
              {r.meta && Object.keys(r.meta).length > 0 && (
                <pre className="text-[10px] text-arena-500 dark:text-neon-200/55 mt-1 whitespace-pre-wrap break-words font-mono">
                  {JSON.stringify(r.meta, null, 0).slice(0, 200)}
                </pre>
              )}
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

// ─── Player action sheet ──────────────────────────────────────────
//
// Same God-Mode verbs the list-card kebab exposes, surfaced from the
// pinned-header kebab. Items map 1:1 with PlayersListPanel's
// PlayerActionSheet so admin doesn't relearn the vocabulary.
function PlayerActionSheet({
  user, isAdmin, onClose, onAdjust, onReassign, onMutate, onDelete,
}: {
  user: UserFull;
  isAdmin: boolean;
  onClose: () => void;
  onAdjust: () => void;
  onReassign: () => void;
  onMutate: (path: string, body: unknown, message: string) => Promise<void>;
  onDelete: () => Promise<void>;
}) {
  const isActive = user.status === 'active';
  const isBanned = user.status === 'banned';
  const emailOk  = !!user.email_verified_at;
  const phoneOk  = !!user.phone_verified_at;
  const hasTg    = !!user.telegram_verified_at;
  const locked   = !!user.locked_until && new Date(user.locked_until) > new Date();

  const groups: ActionGroup[] = [
    {
      title: 'Wallet',
      items: [
        { key: 'adjust', icon: 'account_balance_wallet', label: 'Direct credit / debit',
          hint: user.owner_username
            ? `From platform or @${user.owner_username}'s pool`
            : 'Platform-direct mint / burn',
          onSelect: () => onAdjust() },
        { key: 'reassign', icon: 'swap_horiz', label: 'Reassign agent',
          hint: user.owner_username ? `Currently under @${user.owner_username}` : 'Platform-direct',
          onSelect: () => onReassign() },
      ],
    },
    {
      title: 'Status',
      items: [
        { key: 'activate', icon: 'play_arrow', label: 'Activate', hidden: isActive,
          onSelect: () => onMutate(`/admin/users/${user.id}/status`, { status: 'active' }, 'Activated') },
        { key: 'suspend',  icon: 'block', label: 'Suspend', tone: 'danger' as const, hidden: !isActive,
          confirm: { title: `Suspend @${user.username}?`, body: 'Player signed out + blocked from logging in until reactivated.', cta: 'Suspend', ctaTone: 'danger' as const },
          onSelect: () => onMutate(`/admin/users/${user.id}/status`, { status: 'suspended' }, 'Suspended') },
        { key: 'ban', icon: 'gavel', label: 'Ban', tone: 'danger' as const, hidden: isBanned,
          confirm: { title: `Ban @${user.username}?`, body: 'Stronger than suspend — used for ToS violations.', cta: 'Ban', ctaTone: 'danger' as const },
          onSelect: () => onMutate(`/admin/users/${user.id}/status`, { status: 'banned' }, 'Banned') },
        { key: 'restore', icon: 'restart_alt', label: 'Restore to active', hidden: !isBanned,
          onSelect: () => onMutate(`/admin/users/${user.id}/status`, { status: 'active' }, 'Restored') },
      ],
    },
    {
      title: 'Verification',
      items: [
        { key: 'fv_email', icon: 'mark_email_read', label: 'Force-verify email',
          hint: emailOk ? 'Already verified' : 'Stamps email_verified_at = NOW()', disabled: emailOk,
          onSelect: () => onMutate(`/admin/users/${user.id}/force-verify-email`, undefined, 'Email force-verified') },
        { key: 'fv_phone', icon: 'mark_chat_read', label: 'Force-verify phone',
          hint: phoneOk ? 'Already verified' : 'Stamps phone_verified_at = NOW()', disabled: phoneOk,
          onSelect: () => onMutate(`/admin/users/${user.id}/force-verify-phone`, undefined, 'Phone force-verified') },
        { key: 'tg_unlink', icon: 'link_off', label: 'Unlink Telegram', tone: 'danger' as const,
          hint: hasTg ? 'Clears chat id / username / verified_at' : 'No Telegram link to clear',
          disabled: !hasTg,
          confirm: { title: `Unlink Telegram for @${user.username}?`, body: 'User must re-bind via the bot conversation.', cta: 'Unlink', ctaTone: 'danger' as const },
          onSelect: () => onMutate(`/admin/users/${user.id}/unlink-telegram`, undefined, 'Telegram unlinked') },
      ],
    },
    {
      title: 'Sessions & security',
      items: [
        { key: 'clear_lockout', icon: 'lock_open', label: 'Clear lockout',
          hint: locked ? `Locked until ${new Date(user.locked_until!).toLocaleTimeString('en-IN')}` : 'No active lockout',
          disabled: !locked && user.failed_login_attempts === 0,
          onSelect: () => onMutate(`/admin/users/${user.id}/clear-lockout`, undefined, 'Lockout cleared') },
        { key: 'force_logout', icon: 'logout', label: 'Force logout', tone: 'danger' as const,
          hint: 'Revokes every refresh token',
          confirm: { title: `Force log @${user.username} out everywhere?`, body: 'Sessions terminated; password unchanged.', cta: 'Force logout', ctaTone: 'danger' as const },
          onSelect: () => onMutate(`/admin/users/${user.id}/force-logout`, undefined, 'Logged out everywhere') },
      ],
    },
    ...(isAdmin ? [{
      title: 'Admin only',
      items: [
        { key: 'test', icon: user.is_test ? 'science_off' : 'science',
          label: user.is_test ? 'Unmark test account' : 'Mark as test account',
          hint: user.is_test ? 'Will count in analytics again' : 'Will be hidden from analytics',
          onSelect: () => onMutate(`/admin/users/${user.id}/test-flag`, { is_test: !user.is_test }, user.is_test ? 'Unmarked test' : 'Marked test') },
        { key: 'delete', icon: 'delete_forever', label: 'Delete account', tone: 'danger' as const,
          hint: 'Cascades wallet + tokens. Ledger preserved.',
          confirm: { title: `Permanently delete @${user.username}?`, body: 'Deletes the row + cascades wallet/tokens. Cannot be undone.', cta: 'Delete', ctaTone: 'danger' as const },
          onSelect: () => onDelete() },
      ],
    } satisfies ActionGroup] : []),
  ];

  return (
    <EntityActionSheet
      open
      onClose={onClose}
      title={`@${user.username}`}
      subtitle={[user.first_name, user.last_name].filter(Boolean).join(' ') || `Player #${user.id}`}
      groups={groups}
    />
  );
}
